import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import './Admin.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface User {
  id: number;
  email: string;
  username: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  legacy_realm_id: number | null;
  created_at: string;
  subscription_tier: string | null;
  subscription_status: string | null;
  subscription_override: string | null;
  referral_code: string | null;
  stripe_customer_id: string | null;
}

interface AdminStats {
  totalUsers: number;
  totalRevenue: number;
  activeSubscriptions: number;
  recentSignups: number;
}

interface RevenueBreakdown {
  subscriptions: {
    tiers: { tier: string; count: number; revenue: number }[];
    totalCount: number;
    totalRevenue: number;
  };
  marketplace: {
    items: { product: string; count: number; revenue: number }[];
    totalCount: number;
    totalRevenue: number;
  };
  fetchedAt: string;
}

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  image: string;
  stripe_product_id: string;
  stripe_price_id: string;
  category: string;
  script_string: string;
  is_recurring: boolean;
  is_service: boolean;
  active: boolean;
  display_order: number;
  sale_price_id: string | null;
  sale_price: number | null;
  sale_ends_at: string | null;
  created_at: string;
  updated_at: string;
}

interface DeliveryItem {
  item: number;
  qty: number;
  realm_id: number;
}

interface ServiceQueueItem {
  id: number;
  realm_id: number;
  user_id: number | null;
  user_email: string | null;
  item_name: string;
  stripe_product_id: string | null;
  amount_paid: number;
  quantity: number;
  notes: string | null;
  customer_notes: string | null;
  status: 'backlog' | 'started' | 'blocked' | 'ready' | 'delivered' | 'finished';
  delivery_date: string | null;
  delivery_items: DeliveryItem[];
  created_at: string;
  updated_at: string;
}

interface ServiceCounts {
  backlog: string;
  started: string;
  blocked: string;
  ready: string;
  delivered: string;
  finished: string;
  total: string;
}

interface QueuedScript {
  id: number;
  realm_id: number;
  stripe_customer_id: string | null;
  script_string: string;
  created_at: string;
}

interface UtilizationData {
  timestamp: string;
  playersOnline: number;
}

interface UtilizationSummary {
  totalDataPoints: number;
  averagePlayers: number;
  maxPlayers: number;
  minPlayers: number;
  startDate: string;
  endDate: string;
}

interface XpEvent {
  id: number;
  created_at: string;
  character_id: number;
  customer_id: number;
  xp_awarded: number;
  monster_id: number;
  room_id: number;
  player_level: string;
}

interface XpWindowPlayer {
  character_id: number;
  player_level: string;
  avg_xp_per_window: number;
  active_windows: number;
  total_xp: number;
  total_kills: number;
}

interface XpRoomRange {
  range_start: number;
  range_end: number;
  event_count: number;
  total_xp: number;
  unique_players_max: number;
  sub_ranges: number;
  active_windows: number;
  player_windows: number;
}

interface XpSummary {
  total_events: number;
  unique_players: number;
  unique_monsters: number;
  unique_rooms: number;
  total_xp: number;
  avg_xp_per_kill: number;
}

interface XpAnalyticsData {
  events: XpEvent[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  windowAnalysis: {
    windowMinutes: number;
    lengthHours: number;
    players: XpWindowPlayer[];
  };
  roomRanges: XpRoomRange[];
  summary: XpSummary;
  dateRange: {
    start: string;
    end: string;
  };
}

interface LootEvent {
  id: number;
  created_at: string;
  event_id: number;
  character_id: number;
  customer_id: number;
  data3: number;
  data4: number;
  location: number;
  string1: string;
}

interface LootSummary {
  total_events: number;
  lootbox_won_count: number;
  loot_delivered_count: number;
  unique_players: number;
  common_count: number;
  rare_count: number;
  epic_count: number;
  legendary_count: number;
}

interface LootAnalyticsData {
  events: LootEvent[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  summary: LootSummary;
  dateRange: {
    start: string;
    end: string;
  };
}

interface ItemEvent {
  id: number;
  created_at: string;
  event_id: number;
  character_id: number;
  customer_id: number;
  item_id: number;
  quantity: number;
  location: number;
  string1: string;
}

interface ItemSummary {
  total_events: number;
  items_received_count: number;
  queue_items_count: number;
  unique_players: number;
  total_items: number;
  lootbox_drops_count: number;
}

interface ItemAnalyticsData {
  events: ItemEvent[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  summary: ItemSummary;
  dateRange: {
    start: string;
    end: string;
  };
}

interface PurchaseRecord {
  id: string;
  date: string;
  dateKey: string;
  email: string;
  username: string;
  itemName: string;
  amount: number;
  status: string;
  source: string;
}

interface DailyTotal {
  date: string;
  count: number;
  total: number;
}

interface PurchaseInsightsData {
  purchases: PurchaseRecord[];
  dailyTotals: DailyTotal[];
  summary: {
    totalRevenue: number;
    totalCount: number;
    averagePurchase: number;
    uniqueCustomers: number;
  };
  dateRange: {
    start: string;
    end: string;
  };
}

interface SystemSetting {
  key: string;
  value: string;
  description: string;
  updated_at: string;
}

// Something this API instance is allowed to restart or reboot. Served by
// /api/admin/system/reboot-targets -- see lib/server-reboot.js on the backend.
interface RebootTarget {
  target: 'mud' | 'local' | 'prod';
  kind: 'service' | 'machine' | 'ec2';
  label: string;          // hostname, friendly name of the remote box, or "the MUD"
  ref: string;            // hostname, EC2 instance id, or systemd unit name
  region?: string;
  description: string;
  // 'critical' means this reboots a machine players are on. Note that the LOCAL
  // button is critical when the admin panel is being served from production --
  // severity is a property of the machine, not of which button you pressed.
  // 'medium'/'high' is the service restart: disruptive, but recoverable.
  severity: 'critical' | 'high' | 'medium';
  isProduction: boolean;
  impact: string[];
  contactName: string;
  contactHow: string;
  // Machine reboots demand a typed phrase; the service restart deliberately does
  // not. It is meant to be used, and a phrase typed routinely stops being a
  // speed bump for the buttons that actually need one.
  requiresPhrase: boolean;
  confirmPhrase: string;
  // Present only on the service target: a build staged for promotion. Restarting
  // deploys it, whether or not the person pressing knows it is there.
  stagedBinary?: { path: string; bytes: number; mtime: string } | null;
}

interface RebootLogEntry {
  id: number;
  target: string;
  target_ref: string;
  requested_by_email: string | null;
  status: string;
  error: string | null;
  notes: string | null;
  requested_at: string;
  completed_at: string | null;
}

const Admin: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalRevenue: 0,
    activeSubscriptions: 0,
    recentSignups: 0
  });
  const [revenueBreakdown, setRevenueBreakdown] = useState<RevenueBreakdown | null>(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [products, setProducts] = useState<Product[]>([]);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [selectedUserForScript, setSelectedUserForScript] = useState<User | null>(null);
  const [scriptInput, setScriptInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [subscriptionFilters, setSubscriptionFilters] = useState<Set<string>>(new Set(['none', 'platinum', 'gold', 'silver', 'copper']));
  const [showStaff, setShowStaff] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editUserForm, setEditUserForm] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    password: '',
    referral_code: '',
    legacy_realm_id: '',
    subscription_override: 0  // 0=none, 1=platinum, 2=gold, 3=silver, 4=copper
  });
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [queuedScripts, setQueuedScripts] = useState<QueuedScript[]>([]);
  const [loadingScripts, setLoadingScripts] = useState(false);
  const [editingScriptId, setEditingScriptId] = useState<number | null>(null);
  const [editingScriptValue, setEditingScriptValue] = useState('');
  const [creatingStripeCustomer, setCreatingStripeCustomer] = useState(false);
  const [showUtilizationModal, setShowUtilizationModal] = useState(false);
  const [utilizationData, setUtilizationData] = useState<UtilizationData[]>([]);
  const [utilizationSummary, setUtilizationSummary] = useState<UtilizationSummary | null>(null);
  const [utilizationLoading, setUtilizationLoading] = useState(false);
  const [utilizationDateRange, setUtilizationDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // XP Analytics state
  const [showXpAnalyticsModal, setShowXpAnalyticsModal] = useState(false);
  const [xpAnalyticsData, setXpAnalyticsData] = useState<XpAnalyticsData | null>(null);
  const [xpAnalyticsLoading, setXpAnalyticsLoading] = useState(false);
  const [xpAnalyticsParams, setXpAnalyticsParams] = useState({
    lengthHours: 96,
    windowMinutes: 15,
    page: 1,
    pageSize: 50,
    sortBy: 'created_at',
    sortDir: 'desc' as 'asc' | 'desc',
    playerId: '',
    monsterId: '',
    locationMin: '',
    locationMax: ''
  });
  const [xpActiveTab, setXpActiveTab] = useState<'events' | 'players' | 'rooms'>('players');
  const [xpPlayerSort, setXpPlayerSort] = useState<{ column: keyof XpWindowPlayer; dir: 'asc' | 'desc' }>({ column: 'avg_xp_per_window', dir: 'desc' });
  const [xpRoomSort, setXpRoomSort] = useState<{ column: keyof XpRoomRange; dir: 'asc' | 'desc' }>({ column: 'total_xp', dir: 'desc' });

  // Loot Analytics state
  const [lootAnalyticsData, setLootAnalyticsData] = useState<LootAnalyticsData | null>(null);
  const [lootAnalyticsLoading, setLootAnalyticsLoading] = useState(false);
  const [lootAnalyticsParams, setLootAnalyticsParams] = useState({
    startDate: new Date(Date.now() - 96 * 60 * 60 * 1000).toISOString().slice(0, 16),
    endDate: new Date().toISOString().slice(0, 16),
    page: 1,
    pageSize: 50,
    sortBy: 'created_at',
    sortDir: 'desc' as 'asc' | 'desc',
    playerId: '',
    eventType: ''
  });

  // Item Analytics state
  const [itemAnalyticsData, setItemAnalyticsData] = useState<ItemAnalyticsData | null>(null);
  const [itemAnalyticsLoading, setItemAnalyticsLoading] = useState(false);
  const [itemAnalyticsParams, setItemAnalyticsParams] = useState({
    startDate: new Date(Date.now() - 96 * 60 * 60 * 1000).toISOString().slice(0, 16),
    endDate: new Date().toISOString().slice(0, 16),
    page: 1,
    pageSize: 50,
    sortBy: 'created_at',
    sortDir: 'desc' as 'asc' | 'desc',
    playerId: '',
    eventType: ''
  });

  // Purchase Insights state
  const [purchaseInsightsData, setPurchaseInsightsData] = useState<PurchaseInsightsData | null>(null);
  const [purchaseInsightsLoading, setPurchaseInsightsLoading] = useState(false);
  const [purchaseInsightsParams, setPurchaseInsightsParams] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [hideRaffle, setHideRaffle] = useState(false);

  // Service queue state
  const [services, setServices] = useState<ServiceQueueItem[]>([]);
  const [serviceCounts, setServiceCounts] = useState<ServiceCounts | null>(null);
  const [loadingServices, setLoadingServices] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState<ServiceQueueItem | null>(null);
  const [serviceStatusFilter, setServiceStatusFilter] = useState<string>('all');
  const [serviceSortColumn, setServiceSortColumn] = useState<string>('created_at');
  const [serviceSortDirection, setServiceSortDirection] = useState<'asc' | 'desc'>('desc');

  // Character count tracking for text fields
  const [productDescription, setProductDescription] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [deliveryItems, setDeliveryItems] = useState<DeliveryItem[]>([]);

  // System status monitoring
  const [systemStatus, setSystemStatus] = useState<any>(null);
  const [systemStatusLoading, setSystemStatusLoading] = useState(false);

  // Machine reboots (danger zone). Targets come from the API rather than being
  // hardcoded, so the same build shows the correct hostname wherever it is served.
  const [rebootTargets, setRebootTargets] = useState<RebootTarget[]>([]);
  const [rebootLog, setRebootLog] = useState<RebootLogEntry[]>([]);
  const [pendingReboot, setPendingReboot] = useState<RebootTarget | null>(null);
  const [rebootConfirmText, setRebootConfirmText] = useState('');
  const [rebootAckContact, setRebootAckContact] = useState(false);
  const [rebootInFlight, setRebootInFlight] = useState(false);
  const [rebootError, setRebootError] = useState('');

  // System settings
  const [systemSettings, setSystemSettings] = useState<SystemSetting[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  // Helper function to get subscription type display (0-4: None, Plat, Gold, Silv, Copp)
  const getSubscriptionType = (tier: string | null, status: string | null): string => {
    if (!tier || !status || status !== 'active') {
      return 'None'; // 0
    }

    switch (tier.toLowerCase()) {
      case 'platinum':
        return 'Plat'; // 1
      case 'gold':
        return 'Gold'; // 2
      case 'silver':
        return 'Silv'; // 3
      case 'copper':
        return 'Copp'; // 4
      default:
        return 'None'; // 0
    }
  };

  // Toggle subscription filter checkbox
  const toggleSubscriptionFilter = (filter: string) => {
    setSubscriptionFilters(prev => {
      const newFilters = new Set(prev);
      if (newFilters.has(filter)) {
        newFilters.delete(filter);
      } else {
        newFilters.add(filter);
      }
      return newFilters;
    });
  };

  // Filter users based on search text and subscription filter
  const filteredUsers = users.filter(user => {
    // Staff filter (hide majicrealm.com emails unless showStaff is checked)
    const isStaff = user.email.toLowerCase().includes('majicrealm.com');
    if (isStaff && !showStaff) {
      return false;
    }

    // Subscription filter (check if user's subscription type is in the selected filters)
    const userSubType = getSubscriptionType(user.subscription_tier, user.subscription_status);
    const subTypeToFilter: { [key: string]: string } = {
      'None': 'none',
      'Plat': 'platinum',
      'Gold': 'gold',
      'Silv': 'silver',
      'Copp': 'copper'
    };
    if (!subscriptionFilters.has(subTypeToFilter[userSubType])) {
      return false;
    }

    // Text search filter
    if (searchText.trim() !== '') {
      const searchLower = searchText.toLowerCase();
      const matchesEmail = user.email.toLowerCase().includes(searchLower);
      const matchesFirstName = user.first_name?.toLowerCase().includes(searchLower);
      const matchesLastName = user.last_name?.toLowerCase().includes(searchLower);
      const matchesRealmId = user.legacy_realm_id?.toString().includes(searchLower);
      const matchesRole = user.role.toLowerCase().includes(searchLower);

      return matchesEmail || matchesFirstName || matchesLastName || matchesRealmId || matchesRole;
    }

    return true;
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAdminData();
      // Refresh auth cookie for iframe access
      refreshAuthCookie();
    }
  }, [user]);

  const refreshAuthCookie = async () => {
    try {
      await axios.post(`${API_BASE}/api/auth/refresh-cookie`, {}, {
        withCredentials: true, // Required for cookies to be set
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      console.log('✅ Auth cookie refreshed');
    } catch (error) {
      console.error('Failed to refresh auth cookie:', error);
    }
  };

  // Fetch products and purchase insights when products tab is selected
  useEffect(() => {
    if (activeTab === 'products' && user?.role === 'admin') {
      fetchProducts();
      fetchPurchaseInsights();
    }
  }, [activeTab]);

  // Fetch services when services tab is selected or filter changes
  useEffect(() => {
    if (activeTab === 'services' && user?.role === 'admin') {
      fetchServices();
    }
  }, [activeTab, serviceStatusFilter]);

  // Fetch system status when system tab is selected
  useEffect(() => {
    if (activeTab === 'system' && user?.role === 'admin') {
      fetchSystemStatus();
      fetchRebootTargets();
      fetchRebootLog();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'settings' && user?.role === 'admin') {
      fetchSettings();
    }
  }, [activeTab]);

  // Fetch loot analytics when loot tab is selected
  useEffect(() => {
    if (activeTab === 'loot' && user?.role === 'admin') {
      fetchLootAnalytics();
      fetchItemAnalytics();
    }
  }, [activeTab]);

  const fetchSystemStatus = async () => {
    setSystemStatusLoading(true);
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };
      const response = await axios.get(`${API_BASE}/api/admin/system-status`, authHeaders);
      setSystemStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch system status:', error);
      setSystemStatus({ error: 'Failed to fetch system status' });
    } finally {
      setSystemStatusLoading(false);
    }
  };

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };
      const response = await axios.get(`${API_BASE}/api/admin/settings`, authHeaders);
      setSystemSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setMessage({ type: 'error', text: 'Failed to fetch settings' });
    } finally {
      setSettingsLoading(false);
    }
  };

  const updateSetting = async (key: string, value: string) => {
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };
      await axios.put(`${API_BASE}/api/admin/settings/${key}`, { value }, authHeaders);
      setMessage({ type: 'success', text: `Setting "${key}" updated successfully` });
      fetchSettings();
    } catch (error) {
      console.error('Failed to update setting:', error);
      setMessage({ type: 'error', text: 'Failed to update setting' });
    }
  };

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      // Fetch all data in parallel
      const [usersResponse, statsUsersResponse, statsSubsResponse, statsRevenueResponse, revenueBreakdownResponse] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/users`, authHeaders),
        axios.get(`${API_BASE}/api/admin/stats/users`, authHeaders),
        axios.get(`${API_BASE}/api/admin/stats/subscriptions`, authHeaders),
        axios.get(`${API_BASE}/api/admin/stats/revenue`, authHeaders),
        axios.get(`${API_BASE}/api/admin/revenue-breakdown`, authHeaders)
      ]);

      setUsers(usersResponse.data.users);

      // Calculate recent signups
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const recentSignups = usersResponse.data.users.filter((u: User) =>
        new Date(u.created_at) > oneWeekAgo
      ).length;

      // Set stats with real data
      setStats({
        totalUsers: statsUsersResponse.data.totalUsers,
        totalRevenue: statsRevenueResponse.data.revenue30Days,
        activeSubscriptions: statsSubsResponse.data.activeSubscriptions,
        recentSignups
      });

      setRevenueBreakdown(revenueBreakdownResponse.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      setMessage({ type: 'error', text: 'Failed to load admin data' });
    } finally {
      setLoading(false);
    }
  };

  const openEditUserModal = async (userToEdit: User) => {
    setEditingUser(userToEdit);
    // Map string override value to numeric: platinum=1, gold=2, silver=3, copper=4, nosub=5, else 0
    const overrideToNum: { [key: string]: number } = { platinum: 1, gold: 2, silver: 3, copper: 4, nosub: 5 };
    const overrideNum = userToEdit.subscription_override
      ? overrideToNum[userToEdit.subscription_override.toLowerCase()] || 0
      : 0;
    setEditUserForm({
      first_name: userToEdit.first_name || '',
      last_name: userToEdit.last_name || '',
      username: userToEdit.username || '',
      email: userToEdit.email || '',
      password: '',
      referral_code: userToEdit.referral_code || '',
      legacy_realm_id: userToEdit.legacy_realm_id != null ? String(userToEdit.legacy_realm_id) : '',
      subscription_override: overrideNum
    });
    setShowEditPassword(false);
    setQueuedScripts([]);
    setEditingScriptId(null);
    setShowEditUserModal(true);

    // Fetch queued scripts if user has a realm_id
    if (userToEdit.legacy_realm_id) {
      setLoadingScripts(true);
      try {
        const authHeaders = {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`
          }
        };
        const response = await axios.get(
          `${API_BASE}/api/admin/scripts/realm/${userToEdit.legacy_realm_id}`,
          authHeaders
        );
        setQueuedScripts(response.data.scripts || []);
      } catch (error) {
        console.error('Failed to fetch queued scripts:', error);
      } finally {
        setLoadingScripts(false);
      }
    }
  };

  const handleEditUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      const updateData: any = {
        first_name: editUserForm.first_name,
        last_name: editUserForm.last_name,
        username: editUserForm.username,
        email: editUserForm.email,
        referral_code: editUserForm.referral_code,
        legacy_realm_id: editUserForm.legacy_realm_id.trim() !== '' ? parseInt(editUserForm.legacy_realm_id) : null,
        subscription_override: editUserForm.subscription_override
      };

      // Only include password if it was entered
      if (editUserForm.password.trim() !== '') {
        updateData.password = editUserForm.password;
      }

      await axios.put(`${API_BASE}/api/admin/users/${editingUser.id}`, updateData, authHeaders);

      setMessage({ type: 'success', text: 'User updated successfully!' });
      setShowEditUserModal(false);
      setEditingUser(null);
      fetchAdminData(); // Refresh the user list

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error: any) {
      console.error('Failed to update user:', error);
      const errorMsg = error.response?.data?.error || 'Failed to update user';
      setMessage({ type: 'error', text: errorMsg });
    }
  };

  const handleDeleteUser = async () => {
    if (!editingUser) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete user "${editingUser.email}"?\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      await axios.delete(`${API_BASE}/api/admin/users/${editingUser.id}`, authHeaders);

      setMessage({ type: 'success', text: 'User deleted successfully!' });
      setShowEditUserModal(false);
      setEditingUser(null);
      fetchAdminData(); // Refresh the user list

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      const errorMsg = error.response?.data?.error || 'Failed to delete user';
      setMessage({ type: 'error', text: errorMsg });
    }
  };

  const handleCreateStripeCustomer = async () => {
    if (!editingUser) return;

    setCreatingStripeCustomer(true);
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      const response = await axios.post(
        `${API_BASE}/api/admin/users/${editingUser.id}/ensure-stripe-customer`,
        {},
        authHeaders
      );

      const msg = response.data.created
        ? `Stripe customer created: ${response.data.stripe_customer_id}`
        : `User already has Stripe customer: ${response.data.stripe_customer_id}`;
      setMessage({ type: 'success', text: msg });

      // Update the editingUser state with the new stripe_customer_id
      setEditingUser({ ...editingUser, stripe_customer_id: response.data.stripe_customer_id });

      // Refresh the user list
      fetchAdminData();

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
    } catch (error: any) {
      console.error('Failed to create Stripe customer:', error);
      const errorMsg = error.response?.data?.error || 'Failed to create Stripe customer';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setCreatingStripeCustomer(false);
    }
  };

  const handleUserAction = async (userId: number, action: string) => {
    try {
      setMessage({ type: 'success', text: `User ${action} feature coming soon!` });
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to ${action} user` });
    }
  };

  const handleEditScript = (script: QueuedScript) => {
    setEditingScriptId(script.id);
    setEditingScriptValue(script.script_string);
  };

  const handleSaveScript = async (scriptId: number) => {
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      await axios.put(
        `${API_BASE}/api/admin/scripts/${scriptId}`,
        { script_string: editingScriptValue },
        authHeaders
      );

      // Update local state
      setQueuedScripts(prev =>
        prev.map(s => s.id === scriptId ? { ...s, script_string: editingScriptValue } : s)
      );
      setEditingScriptId(null);
      setEditingScriptValue('');
      setMessage({ type: 'success', text: 'Script updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Failed to update script:', error);
      setMessage({ type: 'error', text: 'Failed to update script' });
    }
  };

  const handleDeleteScript = async (scriptId: number) => {
    if (!window.confirm('Are you sure you want to delete this script from the queue?')) {
      return;
    }

    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      await axios.delete(`${API_BASE}/api/admin/scripts/${scriptId}`, authHeaders);

      // Update local state
      setQueuedScripts(prev => prev.filter(s => s.id !== scriptId));
      setMessage({ type: 'success', text: 'Script deleted successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Failed to delete script:', error);
      setMessage({ type: 'error', text: 'Failed to delete script' });
    }
  };

  const handleCancelEditScript = () => {
    setEditingScriptId(null);
    setEditingScriptValue('');
  };

  const openScriptModal = (user: User) => {
    setSelectedUserForScript(user);
    setScriptInput('2;587;1;0;0;0;@ # add a guitar');
    setShowScriptModal(true);
  };

  const handlePostScript = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUserForScript || !selectedUserForScript.legacy_realm_id) {
      setMessage({ type: 'error', text: 'User does not have a realm ID' });
      return;
    }

    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      await axios.post(
        `${API_BASE}/api/admin/postnextscript`,
        {
          realm_id: selectedUserForScript.legacy_realm_id,
          script: scriptInput
        },
        authHeaders
      );

      setMessage({ type: 'success', text: `Script posted to realm ${selectedUserForScript.legacy_realm_id} successfully!` });
      setShowScriptModal(false);
      setSelectedUserForScript(null);
      setScriptInput('');
    } catch (error) {
      console.error('Failed to post script:', error);
      setMessage({ type: 'error', text: 'Failed to post script' });
    }
  };

  const runSystemCommand = async (command: string) => {
    try {
      setMessage({ type: 'success', text: `System command "${command}" executed successfully!` });
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to execute system command` });
    }
  };

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
  });

  const fetchRebootTargets = async (): Promise<RebootTarget[]> => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/system/reboot-targets`, authHeader());
      const targets: RebootTarget[] = response.data.targets || [];
      setRebootTargets(targets);
      return targets;
    } catch (error) {
      console.error('Failed to fetch reboot targets:', error);
      setRebootTargets([]);
      return [];
    }
  };

  const fetchRebootLog = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/system/reboot-log`, authHeader());
      setRebootLog(response.data.entries || []);
    } catch (error) {
      console.error('Failed to fetch reboot log:', error);
      setRebootLog([]);
    }
  };

  const confirmReboot = async () => {
    if (!pendingReboot) return;

    setRebootInFlight(true);
    setRebootError('');
    try {
      const response = await axios.post(
        `${API_BASE}/api/admin/system/reboot`,
        { target: pendingReboot.target, confirm: rebootConfirmText },
        authHeader()
      );
      // 'degraded' means the restart happened but the realm was not serving yet.
      // Not an error -- but it must not read as an unqualified success either.
      setMessage({
        type: response.data.degraded ? 'error' : 'success',
        text: response.data.message
      });
      closeRebootModal();

      // A local reboot kills the API mid-flight, so there is nothing left to
      // poll -- the browser will simply lose the server. For a remote reboot the
      // dev box stays up, so refresh the status panel once prod has had time to
      // come back and let the admin watch it return.
      if (pendingReboot.kind === 'service') {
        // Already waited for the health check server-side; the answer is current.
        fetchRebootLog();
        fetchSystemStatus();
      } else if (pendingReboot.target !== 'local') {
        fetchRebootLog();
        setTimeout(() => { fetchSystemStatus(); fetchRebootLog(); }, 90000);
      }
    } catch (error: any) {
      // Keep the modal open on failure: the phrase is still typed, and the
      // usual causes (wrong phrase, missing AWS permission) are worth reading
      // next to the button rather than as a toast that scrolls away.
      setRebootError(error.response?.data?.error || 'Reboot request failed');
    } finally {
      setRebootInFlight(false);
    }
  };

  const closeRebootModal = () => {
    setPendingReboot(null);
    setRebootConfirmText('');
    setRebootAckContact(false);
    setRebootError('');
  };

  const openRebootModal = async (target: RebootTarget) => {
    setPendingReboot(target);
    setRebootConfirmText('');
    setRebootAckContact(false);
    setRebootError('');

    // Re-read the targets when opening the MUD dialog: a build can be staged at
    // any time, and the warning is worthless if it reflects whatever was true
    // when the page happened to load. Cheap, and only on the target that cares.
    if (target.kind === 'service') {
      const fresh = await fetchRebootTargets();
      const updated = fresh.find(t => t.target === target.target);
      // Only if the dialog is still the one we opened -- the admin may have
      // cancelled while this was in flight.
      if (updated) {
        setPendingReboot(current =>
          current && current.target === updated.target ? updated : current);
      }
    }
  };

  const handleRefreshStats = async () => {
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      // Trigger the refresh
      await axios.post(`${API_BASE}/api/admin/stats/refresh`, {}, authHeaders);

      setMessage({ type: 'success', text: 'Stats refresh started! Data will update in a few seconds...' });

      // Wait a bit, then reload the data
      setTimeout(async () => {
        await fetchAdminData();
        setMessage({ type: 'success', text: 'Stats refreshed successfully!' });

        // Clear the success message after 3 seconds
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      }, 3000); // Wait 3 seconds for the refresh to complete
    } catch (error) {
      console.error('Failed to refresh stats:', error);
      setMessage({ type: 'error', text: 'Failed to refresh stats' });

      // Clear the error message after 5 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
    }
  };

  // Utilization graph functions
  const fetchUtilizationData = async () => {
    setUtilizationLoading(true);
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      const params = new URLSearchParams({
        startDate: utilizationDateRange.startDate,
        endDate: utilizationDateRange.endDate + 'T23:59:59'
      });

      const response = await axios.get(`${API_BASE}/api/admin/stats/utilization?${params}`, authHeaders);
      setUtilizationData(response.data.data || []);
      setUtilizationSummary(response.data.summary || null);
    } catch (error) {
      console.error('Failed to fetch utilization data:', error);
      setMessage({ type: 'error', text: 'Failed to load utilization data' });
    } finally {
      setUtilizationLoading(false);
    }
  };

  const openUtilizationModal = () => {
    setShowUtilizationModal(true);
    fetchUtilizationData();
  };

  // XP Analytics functions
  const fetchXpAnalytics = async (params = xpAnalyticsParams) => {
    setXpAnalyticsLoading(true);
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      const queryParams = new URLSearchParams({
        lengthHours: params.lengthHours.toString(),
        windowMinutes: params.windowMinutes.toString(),
        page: params.page.toString(),
        pageSize: params.pageSize.toString(),
        sortBy: params.sortBy,
        sortDir: params.sortDir
      });

      if (params.playerId) queryParams.set('playerId', params.playerId);
      if (params.monsterId) queryParams.set('monsterId', params.monsterId);
      if (params.locationMin) queryParams.set('locationMin', params.locationMin);
      if (params.locationMax) queryParams.set('locationMax', params.locationMax);

      const response = await axios.get(`${API_BASE}/api/admin/stats/xp-analytics?${queryParams}`, authHeaders);
      setXpAnalyticsData(response.data);
    } catch (error) {
      console.error('Failed to fetch XP analytics:', error);
      setMessage({ type: 'error', text: 'Failed to load XP analytics data' });
    } finally {
      setXpAnalyticsLoading(false);
    }
  };

  const openXpAnalyticsModal = () => {
    setShowXpAnalyticsModal(true);
    fetchXpAnalytics();
  };

  const handleXpParamChange = (key: string, value: string | number) => {
    const newParams = { ...xpAnalyticsParams, [key]: value };
    if (key !== 'page') {
      newParams.page = 1; // Reset to first page on filter change
    }
    setXpAnalyticsParams(newParams);
  };

  const handleXpSort = (column: string) => {
    const newDir = xpAnalyticsParams.sortBy === column && xpAnalyticsParams.sortDir === 'desc' ? 'asc' : 'desc';
    const newParams = { ...xpAnalyticsParams, sortBy: column, sortDir: newDir as 'asc' | 'desc' };
    setXpAnalyticsParams(newParams);
    fetchXpAnalytics(newParams);
  };

  const handlePlayerSort = (column: keyof XpWindowPlayer) => {
    setXpPlayerSort(prev => ({
      column,
      dir: prev.column === column && prev.dir === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleRoomSort = (column: keyof XpRoomRange) => {
    setXpRoomSort(prev => ({
      column,
      dir: prev.column === column && prev.dir === 'desc' ? 'asc' : 'desc'
    }));
  };

  const getSortedPlayers = () => {
    if (!xpAnalyticsData?.windowAnalysis.players) return [];
    return [...xpAnalyticsData.windowAnalysis.players].sort((a, b) => {
      const aVal = Number(a[xpPlayerSort.column]) || 0;
      const bVal = Number(b[xpPlayerSort.column]) || 0;
      const multiplier = xpPlayerSort.dir === 'desc' ? -1 : 1;
      return (aVal - bVal) * multiplier;
    });
  };

  const getSortedRooms = () => {
    if (!xpAnalyticsData?.roomRanges) return [];
    // All room columns are numeric
    return [...xpAnalyticsData.roomRanges].sort((a, b) => {
      const aVal = a[xpRoomSort.column];
      const bVal = b[xpRoomSort.column];
      const multiplier = xpRoomSort.dir === 'desc' ? -1 : 1;
      return (Number(aVal) - Number(bVal)) * multiplier;
    });
  };

  const handleXpPageChange = (newPage: number) => {
    const newParams = { ...xpAnalyticsParams, page: newPage };
    setXpAnalyticsParams(newParams);
    fetchXpAnalytics(newParams);
  };

  // Loot Analytics functions
  const fetchLootAnalytics = async (params = lootAnalyticsParams) => {
    setLootAnalyticsLoading(true);
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        pageSize: params.pageSize.toString(),
        sortBy: params.sortBy,
        sortDir: params.sortDir
      });

      if (params.startDate) queryParams.set('startDate', new Date(params.startDate).toISOString());
      if (params.endDate) queryParams.set('endDate', new Date(params.endDate).toISOString());
      if (params.playerId) queryParams.set('playerId', params.playerId);
      if (params.eventType) queryParams.set('eventType', params.eventType);

      const response = await axios.get(`${API_BASE}/api/admin/stats/loot-analytics?${queryParams}`, authHeaders);
      setLootAnalyticsData(response.data);
    } catch (error) {
      console.error('Failed to fetch loot analytics:', error);
      setMessage({ type: 'error', text: 'Failed to load loot analytics data' });
    } finally {
      setLootAnalyticsLoading(false);
    }
  };

  const fetchItemAnalytics = async (params = itemAnalyticsParams) => {
    setItemAnalyticsLoading(true);
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        pageSize: params.pageSize.toString(),
        sortBy: params.sortBy,
        sortDir: params.sortDir
      });

      if (params.startDate) queryParams.set('startDate', new Date(params.startDate).toISOString());
      if (params.endDate) queryParams.set('endDate', new Date(params.endDate).toISOString());
      if (params.playerId) queryParams.set('playerId', params.playerId);
      if (params.eventType) queryParams.set('eventType', params.eventType);

      const response = await axios.get(`${API_BASE}/api/admin/stats/item-analytics?${queryParams}`, authHeaders);
      setItemAnalyticsData(response.data);
    } catch (error) {
      console.error('Failed to fetch item analytics:', error);
      setMessage({ type: 'error', text: 'Failed to load item analytics data' });
    } finally {
      setItemAnalyticsLoading(false);
    }
  };

  const handleLootSort = (column: string) => {
    const newDir = lootAnalyticsParams.sortBy === column && lootAnalyticsParams.sortDir === 'desc' ? 'asc' : 'desc';
    const newParams = { ...lootAnalyticsParams, sortBy: column, sortDir: newDir as 'asc' | 'desc' };
    setLootAnalyticsParams(newParams);
    fetchLootAnalytics(newParams);
  };

  const handleLootPageChange = (newPage: number) => {
    const newParams = { ...lootAnalyticsParams, page: newPage };
    setLootAnalyticsParams(newParams);
    fetchLootAnalytics(newParams);
  };

  const handleItemSort = (column: string) => {
    const newDir = itemAnalyticsParams.sortBy === column && itemAnalyticsParams.sortDir === 'desc' ? 'asc' : 'desc';
    const newParams = { ...itemAnalyticsParams, sortBy: column, sortDir: newDir as 'asc' | 'desc' };
    setItemAnalyticsParams(newParams);
    fetchItemAnalytics(newParams);
  };

  const handleItemPageChange = (newPage: number) => {
    const newParams = { ...itemAnalyticsParams, page: newPage };
    setItemAnalyticsParams(newParams);
    fetchItemAnalytics(newParams);
  };

  const getRarityName = (rarity: number | null): string => {
    if (rarity == null) return '—';
    switch (rarity) {
      case 0: return 'Common';
      case 1: return 'Rare';
      case 2: return 'Epic';
      case 3: return 'Legendary';
      default: return `Unknown (${rarity})`;
    }
  };

  const getRarityColor = (rarity: number | null): string => {
    if (rarity == null) return '#555';
    switch (rarity) {
      case 0: return '#aaa';
      case 1: return '#4dabf7';
      case 2: return '#DDA0DD';
      case 3: return '#ffd43b';
      default: return '#888';
    }
  };

  // Product management functions
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };
      const response = await axios.get(`${API_BASE}/api/products`, authHeaders);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setMessage({ type: 'error', text: 'Failed to load products' });
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseInsights = async () => {
    setPurchaseInsightsLoading(true);
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };
      const endOfDay = purchaseInsightsParams.endDate + 'T23:59:59';
      const response = await axios.get(`${API_BASE}/api/admin/stats/purchase-insights`, {
        ...authHeaders,
        params: {
          startDate: purchaseInsightsParams.startDate,
          endDate: endOfDay
        }
      });
      setPurchaseInsightsData(response.data);
    } catch (error) {
      console.error('Failed to fetch purchase insights:', error);
    } finally {
      setPurchaseInsightsLoading(false);
    }
  };

  const handleProductSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const productData = {
      name: formData.get('name'),
      description: formData.get('description'),
      price: parseFloat(formData.get('price') as string),
      image: formData.get('image'),
      stripe_product_id: formData.get('stripe_product_id'),
      stripe_price_id: formData.get('stripe_price_id'),
      category: formData.get('category'),
      script_string: formData.get('script_string'),
      is_recurring: formData.get('is_recurring') === 'on',
      is_service: formData.get('is_service') === 'on',
      active: formData.get('active') === 'on',
      display_order: parseInt(formData.get('display_order') as string) || 0,
      // Sale fields are normally set by create-sale-prices.js; editable here so a
      // sale can be pulled without a deploy. Blank clears the sale.
      sale_price_id: (formData.get('sale_price_id') as string)?.trim() || null,
      sale_price: parseFloat(formData.get('sale_price') as string) || null,
      sale_ends_at: (formData.get('sale_ends_at') as string)?.trim() || null
    };

    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      if (editingProduct) {
        await axios.put(`${API_BASE}/api/admin/products/${editingProduct.id}`, productData, authHeaders);
        setMessage({ type: 'success', text: 'Product updated successfully!' });
      } else {
        await axios.post(`${API_BASE}/api/admin/products`, productData, authHeaders);
        setMessage({ type: 'success', text: 'Product created successfully!' });
      }

      setShowProductForm(false);
      setEditingProduct(null);
      fetchProducts();

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Failed to save product:', error);
      setMessage({ type: 'error', text: 'Failed to save product' });
    }
  };

  const handleDeleteProduct = async (productId: number) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      await axios.delete(`${API_BASE}/api/admin/products/${productId}`, authHeaders);
      setMessage({ type: 'success', text: 'Product deleted successfully!' });
      fetchProducts();

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Failed to delete product:', error);
      setMessage({ type: 'error', text: 'Failed to delete product' });
    }
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setProductDescription(product.description || '');
    setShowProductForm(true);
  };

  const handleCreateProduct = () => {
    setEditingProduct(null);
    setProductDescription('');
    setShowProductForm(true);
  };

  // Service queue functions
  const fetchServices = async () => {
    setLoadingServices(true);
    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };
      const statusParam = serviceStatusFilter !== 'all' ? `?status=${serviceStatusFilter}` : '';
      const [servicesResponse, countsResponse] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/services${statusParam}`, authHeaders),
        axios.get(`${API_BASE}/api/admin/services/count`, authHeaders)
      ]);
      setServices(servicesResponse.data.services || []);
      setServiceCounts(countsResponse.data.counts || null);
    } catch (error) {
      console.error('Failed to fetch services:', error);
      setMessage({ type: 'error', text: 'Failed to load services' });
      setServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  const handleServiceSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const serviceData = {
      realm_id: parseInt(formData.get('realm_id') as string),
      user_id: formData.get('user_id') ? parseInt(formData.get('user_id') as string) : null,
      user_email: formData.get('user_email') || null,
      item_name: formData.get('item_name'),
      stripe_product_id: formData.get('stripe_product_id') || null,
      amount_paid: parseFloat(formData.get('amount_paid') as string) || 0,
      notes: formData.get('notes') || null,
      status: formData.get('status') || 'backlog',
      delivery_date: formData.get('delivery_date') || null,
      delivery_items: deliveryItems.filter(item => item.item > 0 && item.qty > 0 && item.realm_id > 0)
    };

    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      if (editingService) {
        await axios.put(`${API_BASE}/api/admin/services/${editingService.id}`, serviceData, authHeaders);
        setMessage({ type: 'success', text: 'Service updated successfully!' });
      } else {
        await axios.post(`${API_BASE}/api/admin/services`, serviceData, authHeaders);
        setMessage({ type: 'success', text: 'Service created successfully!' });
      }

      setShowServiceForm(false);
      setEditingService(null);
      setDeliveryItems([]);
      fetchServices();

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Failed to save service:', error);
      setMessage({ type: 'error', text: 'Failed to save service' });
    }
  };

  const handleStatusChange = async (serviceId: number, newStatus: string) => {
    try {
      await axios.patch(`${API_BASE}/api/admin/services/${serviceId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      fetchServices();
    } catch (error) {
      console.error('Failed to update status:', error);
      setMessage({ type: 'error', text: 'Failed to update status' });
    }
  };

  const handleDeleteService = async (serviceId: number) => {
    if (!window.confirm('Are you sure you want to delete this service item?')) {
      return;
    }

    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      await axios.delete(`${API_BASE}/api/admin/services/${serviceId}`, authHeaders);
      setMessage({ type: 'success', text: 'Service deleted successfully!' });
      fetchServices();

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Failed to delete service:', error);
      setMessage({ type: 'error', text: 'Failed to delete service' });
    }
  };

  const handleEditService = (service: ServiceQueueItem) => {
    setEditingService(service);
    setAdminNotes(service.notes || '');
    setDeliveryItems(service.delivery_items || []);
    setShowServiceForm(true);
  };

  const handleCreateService = () => {
    setEditingService(null);
    setAdminNotes('');
    setDeliveryItems([]);
    setShowServiceForm(true);
  };

  // Immediate delivery handler (wings button)
  const handleDeliverService = async (service: ServiceQueueItem) => {
    if (service.status !== 'ready') {
      setMessage({ type: 'error', text: 'Service must be in READY status to deliver' });
      return;
    }

    if (!service.delivery_items || service.delivery_items.length === 0) {
      setMessage({ type: 'error', text: 'No delivery items to process' });
      return;
    }

    if (!window.confirm(`Deliver ${service.delivery_items.length} item(s) to realm(s) immediately?`)) {
      return;
    }

    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      };

      const response = await axios.post(`${API_BASE}/api/admin/services/${service.id}/deliver`, {}, authHeaders);
      setMessage({ type: 'success', text: `Delivery completed! ${response.data.scriptsAdded} script(s) added to queue.` });
      fetchServices();

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error: any) {
      console.error('Failed to deliver service:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to deliver service' });
    }
  };

  // Delivery items management
  const addDeliveryItem = () => {
    setDeliveryItems([...deliveryItems, { item: 0, qty: 1, realm_id: editingService?.realm_id || 0 }]);
  };

  const removeDeliveryItem = (index: number) => {
    setDeliveryItems(deliveryItems.filter((_, i) => i !== index));
  };

  const updateDeliveryItem = (index: number, field: keyof DeliveryItem, value: number) => {
    const updated = [...deliveryItems];
    updated[index] = { ...updated[index], [field]: value };
    setDeliveryItems(updated);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'backlog': return '#6c757d';
      case 'started': return '#007bff';
      case 'blocked': return '#dc3545';
      case 'ready': return '#9b59b6';
      case 'delivered': return '#1abc9c';
      case 'finished': return '#28a745';
      default: return '#6c757d';
    }
  };

  // Service table sorting
  const handleServiceSort = (column: string) => {
    if (serviceSortColumn === column) {
      setServiceSortDirection(serviceSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setServiceSortColumn(column);
      setServiceSortDirection('asc');
    }
  };

  const getSortIndicator = (column: string) => {
    if (serviceSortColumn !== column) return ' ↕';
    return serviceSortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const sortedServices = [...services].sort((a, b) => {
    const direction = serviceSortDirection === 'asc' ? 1 : -1;

    switch (serviceSortColumn) {
      case 'id':
        return (a.id - b.id) * direction;
      case 'realm_id':
        return (a.realm_id - b.realm_id) * direction;
      case 'item_name':
        return a.item_name.localeCompare(b.item_name) * direction;
      case 'amount_paid':
        return (Number(a.amount_paid) - Number(b.amount_paid)) * direction;
      case 'quantity':
        return ((a.quantity || 1) - (b.quantity || 1)) * direction;
      case 'status':
        const statusOrder: Record<string, number> = { backlog: 0, started: 1, blocked: 2, ready: 3, delivered: 4, finished: 5 };
        return ((statusOrder[a.status] || 0) - (statusOrder[b.status] || 0)) * direction;
      case 'delivery_date':
        const dateA = a.delivery_date ? new Date(a.delivery_date).getTime() : 0;
        const dateB = b.delivery_date ? new Date(b.delivery_date).getTime() : 0;
        return (dateA - dateB) * direction;
      case 'customer_notes':
        return (a.customer_notes || '').localeCompare(b.customer_notes || '') * direction;
      case 'notes':
        return (a.notes || '').localeCompare(b.notes || '') * direction;
      case 'created_at':
        return (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * direction;
      default:
        return 0;
    }
  });

  // Check if user is admin
  if (!user || user.role !== 'admin') {
    return (
      <div className="admin-page">
        <div className="container">
          <div className="access-denied">
            <h2>🚫 Access Denied</h2>
            <p>Administrator privileges required to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="container">

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="admin-tabs">
          <button
            className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            📊 Dashboard
          </button>
          <button
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            👥 Users
          </button>
          <button
            className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            🛍️ Products
          </button>
          <button
            className={`tab-button ${activeTab === 'services' ? 'active' : ''}`}
            onClick={() => setActiveTab('services')}
          >
            📋 Services {serviceCounts && parseInt(serviceCounts.total) - parseInt(serviceCounts.finished) > 0 && (
              <span style={{
                backgroundColor: '#dc3545',
                color: 'white',
                borderRadius: '10px',
                padding: '2px 8px',
                fontSize: '12px',
                marginLeft: '5px'
              }}>
                {parseInt(serviceCounts.total) - parseInt(serviceCounts.finished)}
              </span>
            )}
          </button>
          <button
            className={`tab-button ${activeTab === 'loot' ? 'active' : ''}`}
            onClick={() => setActiveTab('loot')}
          >
            🎁 Loot Analytics
          </button>
          <button
            className={`tab-button ${activeTab === 'system' ? 'active' : ''}`}
            onClick={() => setActiveTab('system')}
          >
            ⚙️ System
          </button>
          <button
            className="tab-button"
            onClick={() => window.open('/editor/', '_blank')}
          >
            ✏️ Editor ↗
          </button>
          <button
            className="tab-button"
            onClick={() => window.open('/editor/cms/admin', '_blank')}
          >
            📰 CMS ↗
          </button>
          <button
            className="tab-button"
            onClick={() => window.open('/events/', '_blank')}
          >
            📊 Events ↗
          </button>
          <button
            className="tab-button"
            onClick={() => window.open('/contests/', '_blank')}
          >
            🎟️ Contests ↗
          </button>
          <button
            className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            🔧 Settings
          </button>
        </div>

        {activeTab === 'dashboard' && (
          <div className="admin-content">
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem', gap: '10px' }}>
              <button
                onClick={openUtilizationModal}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  backgroundColor: '#2c4a3e',
                  color: '#90EE90',
                  border: '1px solid #3d6b56',
                  borderRadius: '4px'
                }}
              >
                📈 Player Utilization
              </button>
              <button
                onClick={openXpAnalyticsModal}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  backgroundColor: '#3e2c4a',
                  color: '#DDA0DD',
                  border: '1px solid #563d6b',
                  borderRadius: '4px'
                }}
              >
                ⚔️ XP Analytics
              </button>
              <button
                onClick={() => window.open('/search', '_blank')}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  backgroundColor: '#2c4a3e',
                  color: '#90EE90',
                  border: '1px solid #3d6b56',
                  borderRadius: '4px'
                }}
              >
                🔍 Search
              </button>
              <button
                className="refresh-btn"
                onClick={handleRefreshStats}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  backgroundColor: '#4a2c18',
                  color: '#f4d03f',
                  border: '1px solid #8b4513',
                  borderRadius: '4px',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? '⏳ Loading...' : '🔄 Refresh'}
              </button>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-content">
                  <div className="stat-number">{stats.totalUsers}</div>
                  <div className="stat-label">Total Users</div>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-content">
                  <div className="stat-number">${stats.totalRevenue.toFixed(2)}</div>
                  <div className="stat-label">30-Day Revenue</div>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">👑</div>
                <div className="stat-content">
                  <div className="stat-number">{stats.activeSubscriptions}</div>
                  <div className="stat-label">Active Subscriptions</div>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">🆕</div>
                <div className="stat-content">
                  <div className="stat-number">{stats.recentSignups}</div>
                  <div className="stat-label">New Users (7 days)</div>
                </div>
              </div>
            </div>

            <div className="dashboard-grid">
              <div className="dashboard-card">
                <h3>Subscription Revenue (30d)</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #444', textAlign: 'left' }}>
                      <th style={{ padding: '6px 4px' }}>Tier</th>
                      <th style={{ padding: '6px 4px', textAlign: 'right' }}>Charges</th>
                      <th style={{ padding: '6px 4px', textAlign: 'right' }}>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenueBreakdown && revenueBreakdown.subscriptions.tiers.length > 0 ? (
                      <>
                        {revenueBreakdown.subscriptions.tiers.map((row) => (
                          <tr key={row.tier} style={{ borderBottom: '1px solid #2a2a2a' }}>
                            <td style={{ padding: '6px 4px' }}>{row.tier}</td>
                            <td style={{ padding: '6px 4px', textAlign: 'right' }}>{row.count}</td>
                            <td style={{ padding: '6px 4px', textAlign: 'right' }}>${row.revenue.toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: '2px solid #444', fontWeight: 'bold' }}>
                          <td style={{ padding: '6px 4px' }}>Total</td>
                          <td style={{ padding: '6px 4px', textAlign: 'right' }}>{revenueBreakdown.subscriptions.totalCount}</td>
                          <td style={{ padding: '6px 4px', textAlign: 'right' }}>${revenueBreakdown.subscriptions.totalRevenue.toFixed(2)}</td>
                        </tr>
                      </>
                    ) : (
                      <tr><td colSpan={3} style={{ padding: '12px', color: '#888' }}>No subscription charges in last 30 days</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="dashboard-card">
                <h3>Marketplace Revenue (30d)</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #444', textAlign: 'left' }}>
                      <th style={{ padding: '6px 4px' }}>Product</th>
                      <th style={{ padding: '6px 4px', textAlign: 'right' }}>Sales</th>
                      <th style={{ padding: '6px 4px', textAlign: 'right' }}>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenueBreakdown && revenueBreakdown.marketplace.items.length > 0 ? (
                      <>
                        {revenueBreakdown.marketplace.items.map((row) => (
                          <tr key={row.product} style={{ borderBottom: '1px solid #2a2a2a' }}>
                            <td style={{ padding: '6px 4px' }}>{row.product}</td>
                            <td style={{ padding: '6px 4px', textAlign: 'right' }}>{row.count}</td>
                            <td style={{ padding: '6px 4px', textAlign: 'right' }}>${row.revenue.toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: '2px solid #444', fontWeight: 'bold' }}>
                          <td style={{ padding: '6px 4px' }}>Total</td>
                          <td style={{ padding: '6px 4px', textAlign: 'right' }}>{revenueBreakdown.marketplace.totalCount}</td>
                          <td style={{ padding: '6px 4px', textAlign: 'right' }}>${revenueBreakdown.marketplace.totalRevenue.toFixed(2)}</td>
                        </tr>
                      </>
                    ) : (
                      <tr><td colSpan={3} style={{ padding: '12px', color: '#888' }}>No marketplace sales in last 30 days</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="admin-content">
            <div className="users-section">
              <div className="section-header">
                <h2>User Management</h2>
                <button
                  className="refresh-btn"
                  onClick={fetchAdminData}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>

              <div className="filters-container">
                <div className="filter-group">
                  <label htmlFor="search-input">Search:</label>
                  <input
                    id="search-input"
                    type="text"
                    placeholder="Search by name, email, realm ID, or role..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="search-input"
                  />
                </div>

                <div className="filter-group">
                  <label>Subscription:</label>
                  <div className="subscription-checkboxes" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {[
                      { value: 'none', label: 'None' },
                      { value: 'platinum', label: 'Plat' },
                      { value: 'gold', label: 'Gold' },
                      { value: 'silver', label: 'Silv' },
                      { value: 'copper', label: 'Copp' }
                    ].map(({ value, label }) => (
                      <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={subscriptionFilters.has(value)}
                          onChange={() => toggleSubscriptionFilter(value)}
                          style={{ cursor: 'pointer' }}
                        />
                        {label}
                      </label>
                    ))}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', marginLeft: '8px', borderLeft: '1px solid #666', paddingLeft: '12px' }}>
                      <input
                        type="checkbox"
                        checked={showStaff}
                        onChange={() => setShowStaff(!showStaff)}
                        style={{ cursor: 'pointer' }}
                      />
                      Staff
                    </label>
                  </div>
                </div>

                <div className="filter-group">
                  <button
                    className="clear-filters-btn"
                    onClick={() => {
                      setSearchText('');
                      setSubscriptionFilters(new Set(['none', 'platinum', 'gold', 'silver', 'copper']));
                      setShowStaff(false);
                    }}
                    disabled={searchText === '' && subscriptionFilters.size === 5 && !showStaff}
                  >
                    Clear Filters
                  </button>
                </div>

                <div className="filter-results">
                  Showing {filteredUsers.length} of {users.length} users
                </div>
              </div>

              <div className="users-table">
                <table>
                  <thead>
                    <tr>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Email</th>
                      <th>Realm ID</th>
                      <th>Role</th>
                      <th>Subscription</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.id}>
                        <td>{user.first_name || '-'}</td>
                        <td>{user.last_name || '-'}</td>
                        <td>{user.email}</td>
                        <td>{user.legacy_realm_id || '-'}</td>
                        <td>
                          <span className={`role-badge ${user.role}`}>
                            {user.role}
                          </span>
                        </td>
                        <td>
                          {user.subscription_override ? (
                            <span className="subscription-badge override" style={{ color: '#ff4444', fontWeight: 'bold' }}>
                              {user.subscription_override}
                            </span>
                          ) : (
                            <span className={`subscription-badge ${user.subscription_tier || 'none'}`}>
                              {getSubscriptionType(user.subscription_tier, user.subscription_status)}
                            </span>
                          )}
                        </td>
                        <td>{new Date(user.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="user-actions">
                            <button
                              className="action-btn gift"
                              onClick={() => openScriptModal(user)}
                              title="Send script to realm"
                              disabled={!user.legacy_realm_id}
                            >
                              🎁
                            </button>
                            <button
                              className="action-btn edit"
                              onClick={() => openEditUserModal(user)}
                              title="Edit user"
                            >
                              ✏️
                            </button>
                            <button
                              className="action-btn ban"
                              onClick={() => handleUserAction(user.id, 'ban')}
                            >
                              🚫
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="admin-content">
            <div className="products-section">
              <div className="section-header">
                <h2>Product Management</h2>
                <button
                  className="create-btn"
                  onClick={handleCreateProduct}
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    fontSize: '16px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  ➕ Create Product
                </button>
              </div>

              {loading && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#f4d03f' }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
                  <div>Loading products...</div>
                </div>
              )}

              {!loading && showProductForm && (
                <div className="product-form-overlay" style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1000
                }}>
                  <div className="product-form-container" style={{
                    backgroundColor: 'white',
                    color: '#333',
                    padding: '30px',
                    borderRadius: '10px',
                    maxWidth: '600px',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    width: '90%'
                  }}>
                    <h3>{editingProduct ? 'Edit Product' : 'Create New Product'}</h3>
                    <form onSubmit={handleProductSubmit}>
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Name *</label>
                        <input type="text" name="name" defaultValue={editingProduct?.name} required style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Description</label>
                        <textarea
                          name="description"
                          value={productDescription}
                          onChange={(e) => setProductDescription(e.target.value)}
                          maxLength={500}
                          rows={3}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                        <div style={{ fontSize: '12px', color: productDescription.length > 450 ? '#dc3545' : '#666', textAlign: 'right', marginTop: '4px' }}>
                          {500 - productDescription.length} characters remaining
                        </div>
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Price *</label>
                        <input type="number" name="price" step="0.01" defaultValue={editingProduct?.price} required style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Image URL</label>
                        <input type="text" name="image" defaultValue={editingProduct?.image} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Stripe Product ID *</label>
                        <input type="text" name="stripe_product_id" defaultValue={editingProduct?.stripe_product_id} required style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Stripe Price ID *</label>
                        <input type="text" name="stripe_price_id" defaultValue={editingProduct?.stripe_price_id} required style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                      </div>

                      <div style={{ marginBottom: '15px', padding: '12px', background: '#fdf6f5', border: '1px solid #eecfcb', borderRadius: '4px' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Sale (optional)</div>
                        <small style={{ color: '#666', display: 'block', marginBottom: '10px' }}>
                          A sale is a second Stripe Price, not a coupon — the Platinum 10% coupon
                          still applies on top of it. Usually set by create-sale-prices.js; clear
                          the Price ID to end the sale.
                        </small>

                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Sale Stripe Price ID</label>
                        <input type="text" name="sale_price_id" defaultValue={editingProduct?.sale_price_id ?? ''} placeholder="price_..." style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', marginBottom: '10px' }} />

                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Sale Price (display)</label>
                        <input type="number" step="0.01" name="sale_price" defaultValue={editingProduct?.sale_price ?? ''} placeholder="4.00" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', marginBottom: '10px' }} />

                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Sale Ends</label>
                        <input type="datetime-local" name="sale_ends_at" defaultValue={editingProduct?.sale_ends_at ? editingProduct.sale_ends_at.slice(0, 16) : ''} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                        <small style={{ color: '#666' }}>Leave blank to run until cleared</small>
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Category *</label>
                        <select name="category" defaultValue={editingProduct?.category} required style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}>
                          <option value="">Select a category</option>
                          <option value="subscriptions">Subscriptions</option>
                          <option value="consumables">Consumables</option>
                          <option value="tickets">Tickets</option>
                          <option value="real estate">Real Estate</option>
                          <option value="pets">Pets</option>
                          <option value="gang services">Gang Services</option>
                          <option value="miscellaneous">Miscellaneous</option>
                        </select>
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Script String (leave empty for service items)</label>
                        <textarea name="script_string" defaultValue={editingProduct?.script_string} rows={3} placeholder="e.g., 1;2;200 #add 200 of item 2 (gold)" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'monospace' }} />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Display Order</label>
                        <input type="number" name="display_order" defaultValue={editingProduct?.display_order ?? 0} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                        <small style={{ color: '#666' }}>Lower numbers appear first in the marketplace</small>
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input type="checkbox" name="is_recurring" defaultChecked={editingProduct?.is_recurring} />
                          <span>Recurring Subscription</span>
                        </label>
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input type="checkbox" name="is_service" defaultChecked={editingProduct?.is_service} />
                          <span>Service Item (no script - goes to service queue)</span>
                        </label>
                      </div>

                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input type="checkbox" name="active" defaultChecked={editingProduct?.active !== false} />
                          <span>Active</span>
                        </label>
                      </div>

                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => { setShowProductForm(false); setEditingProduct(null); }}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#ccc',
                            color: '#333',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                          }}
                        >
                          {editingProduct ? 'Update' : 'Create'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {!loading && (
                <div className="products-table" style={{ marginTop: '20px' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Price</th>
                        <th>Type</th>
                        <th>Script</th>
                        <th>Active</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map(product => (
                      <tr key={product.id}>
                        <td>{product.display_order}</td>
                        <td>{product.name}</td>
                        <td>{product.category}</td>
                        <td>${typeof product.price === 'number' ? product.price.toFixed(2) : parseFloat(product.price).toFixed(2)}</td>
                        <td>{product.is_service ? 'Service' : product.is_recurring ? 'Recurring' : 'One-time'}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {product.script_string || '-'}
                        </td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor: product.active ? '#4CAF50' : '#f44336',
                            color: 'white'
                          }}>
                            {product.active ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => handleEditProduct(product)}
                            style={{
                              padding: '5px 10px',
                              marginRight: '5px',
                              backgroundColor: '#2196F3',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product.id)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                    </tbody>
                  </table>
                  {products.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                      No products found. Create your first product to get started!
                    </div>
                  )}
                </div>
              )}

              {/* Purchase Insights Section */}
              <div style={{
                marginTop: '30px',
                border: '1px solid #444',
                borderRadius: '8px',
                padding: '20px',
                borderLeft: '3px solid #ffd43b'
              }}>
                <h3 style={{ margin: '0 0 15px 0', color: '#ffd43b' }}>Purchase Insights</h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap' }}>
                  <label style={{ color: '#ccc' }}>From:</label>
                  <input
                    type="date"
                    value={purchaseInsightsParams.startDate}
                    onChange={(e) => setPurchaseInsightsParams(prev => ({ ...prev, startDate: e.target.value }))}
                    style={{
                      padding: '6px 10px',
                      backgroundColor: '#2a2a2a',
                      color: '#fff',
                      border: '1px solid #555',
                      borderRadius: '4px'
                    }}
                  />
                  <label style={{ color: '#ccc' }}>To:</label>
                  <input
                    type="date"
                    value={purchaseInsightsParams.endDate}
                    onChange={(e) => setPurchaseInsightsParams(prev => ({ ...prev, endDate: e.target.value }))}
                    style={{
                      padding: '6px 10px',
                      backgroundColor: '#2a2a2a',
                      color: '#fff',
                      border: '1px solid #555',
                      borderRadius: '4px'
                    }}
                  />
                  <button
                    onClick={fetchPurchaseInsights}
                    disabled={purchaseInsightsLoading}
                    style={{
                      padding: '6px 16px',
                      backgroundColor: '#ffd43b',
                      color: '#000',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: purchaseInsightsLoading ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      opacity: purchaseInsightsLoading ? 0.6 : 1
                    }}
                  >
                    {purchaseInsightsLoading ? 'Loading...' : 'Update'}
                  </button>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#ccc', cursor: 'pointer', marginLeft: '10px' }}>
                    <input
                      type="checkbox"
                      checked={hideRaffle}
                      onChange={(e) => setHideRaffle(e.target.checked)}
                    />
                    Hide raffle items
                  </label>
                </div>

                {purchaseInsightsData && (() => {
                  const filtered = hideRaffle
                    ? purchaseInsightsData.purchases.filter(p => !p.itemName.toLowerCase().includes('raffle'))
                    : purchaseInsightsData.purchases;
                  const filteredRevenue = filtered.reduce((sum, p) => sum + p.amount, 0);
                  const filteredUnique = new Set(filtered.map(p => p.email).filter(Boolean)).size;
                  return (
                  <>
                    {/* Summary Cards */}
                    <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1', minWidth: '140px', backgroundColor: '#1a1a2e', padding: '15px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4CAF50' }}>
                          ${filteredRevenue.toFixed(2)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>Total Revenue</div>
                      </div>
                      <div style={{ flex: '1', minWidth: '140px', backgroundColor: '#1a1a2e', padding: '15px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196F3' }}>
                          {filtered.length}
                        </div>
                        <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>Purchases</div>
                      </div>
                      <div style={{ flex: '1', minWidth: '140px', backgroundColor: '#1a1a2e', padding: '15px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff9800' }}>
                          ${filtered.length > 0 ? (filteredRevenue / filtered.length).toFixed(2) : '0.00'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>Avg Purchase</div>
                      </div>
                      <div style={{ flex: '1', minWidth: '140px', backgroundColor: '#1a1a2e', padding: '15px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#9c27b0' }}>
                          {filteredUnique}
                        </div>
                        <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>Unique Customers</div>
                      </div>
                    </div>

                    {/* Purchases Table */}
                    {filtered.length > 0 ? (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #444' }}>
                              <th style={{ padding: '10px', textAlign: 'left', color: '#ccc' }}>Date</th>
                              <th style={{ padding: '10px', textAlign: 'left', color: '#ccc' }}>Customer</th>
                              <th style={{ padding: '10px', textAlign: 'left', color: '#ccc' }}>Item</th>
                              <th style={{ padding: '10px', textAlign: 'right', color: '#ccc' }}>Amount</th>
                              <th style={{ padding: '10px', textAlign: 'center', color: '#ccc' }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.map((purchase) => (
                              <tr key={purchase.id} style={{ borderBottom: '1px solid #333' }}>
                                <td style={{ padding: '8px 10px', color: '#aaa', whiteSpace: 'nowrap' }}>
                                  {new Date(purchase.date).toLocaleDateString()}
                                </td>
                                <td style={{ padding: '8px 10px' }}>
                                  {purchase.username || purchase.email || 'Unknown'}
                                </td>
                                <td style={{ padding: '8px 10px', color: '#ccc' }}>
                                  {purchase.itemName || '—'}
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#4CAF50', fontWeight: 'bold' }}>
                                  ${purchase.amount.toFixed(2)}
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                  <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    backgroundColor: purchase.status === 'instant' ? '#006064' :
                                                     purchase.status === 'finished' ? '#1b5e20' :
                                                     purchase.status === 'delivered' ? '#0d47a1' :
                                                     purchase.status === 'backlog' ? '#555' :
                                                     purchase.status === 'started' ? '#e65100' :
                                                     purchase.status === 'blocked' ? '#b71c1c' :
                                                     purchase.status === 'ready' ? '#4a148c' : '#444',
                                    color: '#fff'
                                  }}>
                                    {purchase.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
                        No non-subscription purchases found in this date range.
                      </div>
                    )}
                  </>
                  );
                })()}

                {!purchaseInsightsData && !purchaseInsightsLoading && (
                  <div style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
                    Loading purchase data...
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="admin-content">
            <div className="services-section">
              <div className="section-header">
                <h2>Service Queue</h2>
                <button
                  className="create-btn"
                  onClick={handleCreateService}
                  disabled={loadingServices}
                  style={{
                    padding: '10px 20px',
                    fontSize: '16px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: loadingServices ? 'not-allowed' : 'pointer',
                    opacity: loadingServices ? 0.6 : 1
                  }}
                >
                  ➕ Create Service
                </button>
              </div>

              {/* Status counts */}
              {serviceCounts && (
                <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  <div style={{ padding: '10px 20px', backgroundColor: '#6c757d', borderRadius: '5px', color: 'white' }}>
                    Backlog: {serviceCounts.backlog}
                  </div>
                  <div style={{ padding: '10px 20px', backgroundColor: '#007bff', borderRadius: '5px', color: 'white' }}>
                    Started: {serviceCounts.started}
                  </div>
                  <div style={{ padding: '10px 20px', backgroundColor: '#dc3545', borderRadius: '5px', color: 'white' }}>
                    Blocked: {serviceCounts.blocked}
                  </div>
                  <div style={{ padding: '10px 20px', backgroundColor: '#9b59b6', borderRadius: '5px', color: 'white' }}>
                    Ready: {serviceCounts.ready}
                  </div>
                  <div style={{ padding: '10px 20px', backgroundColor: '#1abc9c', borderRadius: '5px', color: 'white' }}>
                    Delivered: {serviceCounts.delivered}
                  </div>
                  <div style={{ padding: '10px 20px', backgroundColor: '#28a745', borderRadius: '5px', color: 'white' }}>
                    Finished: {serviceCounts.finished}
                  </div>
                </div>
              )}

              {/* Status filter */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Filter by status:</label>
                <select
                  value={serviceStatusFilter}
                  onChange={(e) => setServiceStatusFilter(e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="all">All</option>
                  <option value="backlog">Backlog</option>
                  <option value="started">Started</option>
                  <option value="blocked">Blocked</option>
                  <option value="ready">Ready</option>
                  <option value="delivered">Delivered</option>
                  <option value="finished">Finished</option>
                </select>
              </div>

              {loadingServices && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#f4d03f' }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
                  <div>Loading services...</div>
                </div>
              )}

              {/* Service form modal */}
              {!loadingServices && showServiceForm && (
                <div className="service-form-overlay" style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1000
                }}>
                  <div className="service-form-container" style={{
                    backgroundColor: 'white',
                    color: '#333',
                    padding: '30px',
                    borderRadius: '10px',
                    maxWidth: '600px',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    width: '90%'
                  }}>
                    <h3>{editingService ? 'Edit Service' : 'Create New Service'}</h3>
                    <form onSubmit={handleServiceSubmit}>
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Realm ID *</label>
                        <input type="number" name="realm_id" defaultValue={editingService?.realm_id} required style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>User ID</label>
                        <input type="number" name="user_id" defaultValue={editingService?.user_id || ''} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>User Email</label>
                        <input type="email" name="user_email" defaultValue={editingService?.user_email || ''} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Item Name *</label>
                        <input type="text" name="item_name" defaultValue={editingService?.item_name} required style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Stripe Product ID</label>
                        <input type="text" name="stripe_product_id" defaultValue={editingService?.stripe_product_id || ''} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Amount Paid *</label>
                        <input type="number" name="amount_paid" step="0.01" defaultValue={editingService?.amount_paid} required style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Status</label>
                        <select name="status" defaultValue={editingService?.status || 'backlog'} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}>
                          <option value="backlog">Backlog</option>
                          <option value="started">Started</option>
                          <option value="blocked">Blocked</option>
                          <option value="ready">Ready</option>
                          <option value="delivered">Delivered</option>
                          <option value="finished">Finished</option>
                        </select>
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Delivery Date & Time</label>
                        <input
                          type="datetime-local"
                          name="delivery_date"
                          defaultValue={editingService?.delivery_date ? editingService.delivery_date.slice(0, 16) : ''}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                      </div>

                      {editingService?.customer_notes && (
                        <div style={{ marginBottom: '15px' }}>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Customer Notes (read-only)</label>
                          <div style={{
                            padding: '8px',
                            backgroundColor: '#f5f5f5',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            whiteSpace: 'pre-wrap',
                            minHeight: '60px'
                          }}>
                            {editingService.customer_notes}
                          </div>
                        </div>
                      )}

                      {/* Delivery Items Management */}
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                          Delivery Items ({deliveryItems.length})
                        </label>
                        <div style={{
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          padding: '10px',
                          backgroundColor: '#f9f9f9'
                        }}>
                          {deliveryItems.length === 0 ? (
                            <div style={{ color: '#666', fontStyle: 'italic', marginBottom: '10px' }}>
                              No delivery items added yet
                            </div>
                          ) : (
                            <div style={{ marginBottom: '10px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 40px', gap: '8px', marginBottom: '5px', fontWeight: 'bold', fontSize: '12px' }}>
                                <span>Item ID</span>
                                <span>Qty</span>
                                <span>Realm ID</span>
                                <span></span>
                              </div>
                              {deliveryItems.map((item, index) => (
                                <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 40px', gap: '8px', marginBottom: '5px' }}>
                                  <input
                                    type="number"
                                    min="1"
                                    max="65535"
                                    value={item.item || ''}
                                    onChange={(e) => updateDeliveryItem(index, 'item', parseInt(e.target.value) || 0)}
                                    placeholder="Item ID (1-65535)"
                                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }}
                                  />
                                  <input
                                    type="number"
                                    min="1"
                                    value={item.qty || ''}
                                    onChange={(e) => updateDeliveryItem(index, 'qty', parseInt(e.target.value) || 0)}
                                    placeholder="Qty"
                                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }}
                                  />
                                  <input
                                    type="number"
                                    min="1"
                                    value={item.realm_id || ''}
                                    onChange={(e) => updateDeliveryItem(index, 'realm_id', parseInt(e.target.value) || 0)}
                                    placeholder="Realm"
                                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeDeliveryItem(index)}
                                    style={{
                                      padding: '6px',
                                      backgroundColor: '#dc3545',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    X
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={addDeliveryItem}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#17a2b8',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            + Add Item
                          </button>
                        </div>
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Admin Notes</label>
                        <textarea
                          name="notes"
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                          maxLength={500}
                          rows={4}
                          placeholder="Internal notes for staff about this service..."
                          style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                        <div style={{ fontSize: '12px', color: adminNotes.length > 450 ? '#dc3545' : '#666', textAlign: 'right', marginTop: '4px' }}>
                          {500 - adminNotes.length} characters remaining
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => { setShowServiceForm(false); setEditingService(null); }}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#ccc',
                            color: '#333',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                          }}
                        >
                          {editingService ? 'Update' : 'Create'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Services table */}
              {!loadingServices && (
                <div className="services-table" style={{ marginTop: '20px', overflowX: 'auto' }}>
                  <table style={{ width: '100%', tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th onClick={() => handleServiceSort('id')} style={{ cursor: 'pointer', width: '40px' }}>ID{getSortIndicator('id')}</th>
                        <th onClick={() => handleServiceSort('realm_id')} style={{ cursor: 'pointer', width: '50px' }}>Realm{getSortIndicator('realm_id')}</th>
                        <th onClick={() => handleServiceSort('item_name')} style={{ cursor: 'pointer', width: '150px' }}>Item{getSortIndicator('item_name')}</th>
                        <th onClick={() => handleServiceSort('quantity')} style={{ cursor: 'pointer', width: '40px' }}>Qty{getSortIndicator('quantity')}</th>
                        <th onClick={() => handleServiceSort('amount_paid')} style={{ cursor: 'pointer', width: '70px' }}>Amount{getSortIndicator('amount_paid')}</th>
                        <th onClick={() => handleServiceSort('status')} style={{ cursor: 'pointer', width: '70px' }}>Status{getSortIndicator('status')}</th>
                        <th onClick={() => handleServiceSort('delivery_date')} style={{ cursor: 'pointer', width: '70px' }}>Delivery{getSortIndicator('delivery_date')}</th>
                        <th onClick={() => handleServiceSort('customer_notes')} style={{ cursor: 'pointer', width: '15%' }}>Customer Notes{getSortIndicator('customer_notes')}</th>
                        <th style={{ width: '120px' }}>Delivery Items</th>
                        <th onClick={() => handleServiceSort('notes')} style={{ cursor: 'pointer', width: '15%' }}>Admin Notes{getSortIndicator('notes')}</th>
                        <th onClick={() => handleServiceSort('created_at')} style={{ cursor: 'pointer', width: '70px' }}>Created{getSortIndicator('created_at')}</th>
                        <th style={{ width: '70px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedServices.map(service => (
                        <tr key={service.id}>
                          <td>{service.id}</td>
                          <td>{service.realm_id}</td>
                          <td>
                            <div>{service.item_name}</div>
                            {service.user_email && (
                              <div style={{ fontSize: '12px', color: '#666' }}>{service.user_email}</div>
                            )}
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: service.quantity > 1 ? 'bold' : 'normal', color: service.quantity > 1 ? '#8b4513' : 'inherit' }}>
                            {service.quantity || 1}
                          </td>
                          <td>${typeof service.amount_paid === 'number' ? service.amount_paid.toFixed(2) : parseFloat(service.amount_paid).toFixed(2)}</td>
                          <td>
                            <select
                              value={service.status}
                              onChange={(e) => handleStatusChange(service.id, e.target.value)}
                              style={{
                                padding: '4px 6px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                backgroundColor: getStatusColor(service.status),
                                color: 'white',
                                border: '1px solid transparent',
                                cursor: 'pointer',
                                textTransform: 'capitalize',
                                outline: 'none'
                              }}
                            >
                              <option value="backlog">Backlog</option>
                              <option value="started">Started</option>
                              <option value="blocked">Blocked</option>
                              <option value="ready">Ready</option>
                              <option value="delivered">Delivered</option>
                              <option value="finished">Finished</option>
                            </select>
                          </td>
                          <td style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>
                            {service.delivery_date ? (
                              <>
                                {new Date(service.delivery_date).toLocaleDateString()}
                                <br />
                                <span style={{ color: '#666' }}>{new Date(service.delivery_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </>
                            ) : '-'}
                          </td>
                          <td style={{ minWidth: '150px', maxWidth: '250px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {service.customer_notes || '-'}
                          </td>
                          <td style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                            {service.delivery_items && service.delivery_items.length > 0 ? (
                              <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                                {service.delivery_items.map((item, idx) => (
                                  <div key={idx} style={{ marginBottom: '2px', padding: '2px 4px', backgroundColor: '#2a2a3e', color: '#f0f0f0', borderRadius: '3px', border: '1px solid #444' }}>
                                    R:{item.realm_id} I:{item.item} x{item.qty}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: '#666' }}>-</span>
                            )}
                          </td>
                          <td style={{ minWidth: '150px', maxWidth: '250px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {service.notes || '-'}
                          </td>
                          <td>{new Date(service.created_at).toLocaleDateString()}</td>
                          <td>
                            <button
                              onClick={() => handleEditService(service)}
                              style={{
                                padding: '5px 10px',
                                marginRight: '5px',
                                backgroundColor: '#2196F3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                              title="Edit service"
                            >
                              ✏️
                            </button>
                            {service.status === 'ready' && service.delivery_items && service.delivery_items.length > 0 && (
                              <button
                                onClick={() => handleDeliverService(service)}
                                style={{
                                  padding: '5px 10px',
                                  marginRight: '5px',
                                  backgroundColor: '#9b59b6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                                title="Deliver immediately"
                              >
                                🚀
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteService(service.id)}
                              style={{
                                padding: '5px 10px',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                              title="Delete service"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {services.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                      No services found. Services will appear here when customers purchase service items.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'system' && (
          <div className="admin-content">
            {/* System Status Monitoring */}
            <div className="system-section" style={{ marginBottom: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h2>System Status</h2>
                <button
                  className="command-btn"
                  onClick={fetchSystemStatus}
                  disabled={systemStatusLoading}
                  style={{ padding: '8px 16px' }}
                >
                  {systemStatusLoading ? 'Refreshing...' : '🔄 Refresh'}
                </button>
              </div>

              {systemStatusLoading && !systemStatus ? (
                <p style={{ color: '#aaa' }}>Loading system status...</p>
              ) : systemStatus?.error ? (
                <p style={{ color: '#ff6b6b' }}>{systemStatus.error}</p>
              ) : systemStatus ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
                  {/* Local Server Status */}
                  <div style={{
                    backgroundColor: '#1a1a2e',
                    borderRadius: '8px',
                    padding: '20px',
                    border: `2px solid ${systemStatus.local?.overall === 'ok' ? '#4ade80' : systemStatus.local?.overall === 'error' ? '#ff6b6b' : '#666'}`
                  }}>
                    <h3 style={{ marginBottom: '10px', color: '#fff' }}>
                      📍 {systemStatus.local?.server || 'Local'} Server
                      <span style={{
                        marginLeft: '10px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        backgroundColor: systemStatus.local?.overall === 'ok' ? '#4ade80' : '#ff6b6b',
                        color: '#000'
                      }}>
                        {systemStatus.local?.overall?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </h3>
                    {systemStatus.local?.timestamp && (
                      <p style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>
                        Last check: {new Date(systemStatus.local.timestamp).toLocaleString()}
                      </p>
                    )}
                    {systemStatus.local?.checks && (
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {Object.entries(systemStatus.local.checks).map(([name, check]: [string, any]) => (
                          <div key={name} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            backgroundColor: '#252540',
                            borderRadius: '4px',
                            borderLeft: `3px solid ${check.status === 'ok' ? '#4ade80' : check.status === 'warning' ? '#fbbf24' : '#ff6b6b'}`
                          }}>
                            <span style={{ color: '#ddd' }}>{name}</span>
                            <span style={{
                              color: check.status === 'ok' ? '#4ade80' : check.status === 'warning' ? '#fbbf24' : '#ff6b6b',
                              fontSize: '13px'
                            }}>
                              {check.status === 'ok' ? '✓' : check.status === 'warning' ? '⚠' : '✗'} {check.message?.substring(0, 40)}
                              {check.responseTime && ` (${check.responseTime}ms)`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {systemStatus.local?.awsrealmStatus?.checks && (
                      <>
                        <h4 style={{ marginTop: '15px', marginBottom: '10px', color: '#aaa' }}>AWS Internal Checks:</h4>
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {Object.entries(systemStatus.local.awsrealmStatus.checks).map(([name, check]: [string, any]) => (
                            <div key={name} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '8px 12px',
                              backgroundColor: '#252540',
                              borderRadius: '4px',
                              borderLeft: `3px solid ${check.status === 'ok' ? '#4ade80' : check.status === 'warning' ? '#fbbf24' : '#ff6b6b'}`
                            }}>
                              <span style={{ color: '#ddd' }}>{name}</span>
                              <span style={{
                                color: check.status === 'ok' ? '#4ade80' : check.status === 'warning' ? '#fbbf24' : '#ff6b6b',
                                fontSize: '13px'
                              }}>
                                {check.status === 'ok' ? '✓' : check.status === 'warning' ? '⚠' : '✗'} {check.message?.substring(0, 40)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Remote Server Status */}
                  <div style={{
                    backgroundColor: '#1a1a2e',
                    borderRadius: '8px',
                    padding: '20px',
                    border: `2px solid ${systemStatus.remote?.overall === 'ok' ? '#4ade80' : systemStatus.remote?.overall === 'error' ? '#ff6b6b' : '#666'}`
                  }}>
                    <h3 style={{ marginBottom: '10px', color: '#fff' }}>
                      ☁️ {systemStatus.remote?.server || 'Remote'} Server
                      <span style={{
                        marginLeft: '10px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        backgroundColor: systemStatus.remote?.overall === 'ok' ? '#4ade80' : systemStatus.remote?.error ? '#666' : '#ff6b6b',
                        color: systemStatus.remote?.error ? '#fff' : '#000'
                      }}>
                        {systemStatus.remote?.error ? 'UNAVAILABLE' : systemStatus.remote?.overall?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </h3>
                    {systemStatus.remote?.error ? (
                      <p style={{ color: '#ff6b6b' }}>{systemStatus.remote.error}</p>
                    ) : (
                      <>
                        {systemStatus.remote?.timestamp && (
                          <p style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>
                            Last check: {new Date(systemStatus.remote.timestamp).toLocaleString()}
                          </p>
                        )}
                        {systemStatus.remote?.checks && (
                          <div style={{ display: 'grid', gap: '8px' }}>
                            {Object.entries(systemStatus.remote.checks).map(([name, check]: [string, any]) => (
                              <div key={name} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px 12px',
                                backgroundColor: '#252540',
                                borderRadius: '4px',
                                borderLeft: `3px solid ${check.status === 'ok' ? '#4ade80' : check.status === 'warning' ? '#fbbf24' : '#ff6b6b'}`
                              }}>
                                <span style={{ color: '#ddd' }}>{name}</span>
                                <span style={{
                                  color: check.status === 'ok' ? '#4ade80' : check.status === 'warning' ? '#fbbf24' : '#ff6b6b',
                                  fontSize: '13px'
                                }}>
                                  {check.status === 'ok' ? '✓' : check.status === 'warning' ? '⚠' : '✗'} {check.message?.substring(0, 40)}
                                  {check.responseTime && ` (${check.responseTime}ms)`}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <p style={{ color: '#aaa' }}>No status data available. Click Refresh to load.</p>
              )}
            </div>

            <div className="system-section">
              <h2>System Commands</h2>
              <div className="commands-grid">
                <div className="command-card">
                  <h3>💾 Database</h3>
                  <div className="command-buttons">
                    <button 
                      className="command-btn"
                      onClick={() => runSystemCommand('backup_database')}
                    >
                      Backup Database
                    </button>
                    <button 
                      className="command-btn"
                      onClick={() => runSystemCommand('optimize_database')}
                    >
                      Optimize Database
                    </button>
                  </div>
                </div>

                <div className="command-card">
                  <h3>🔄 Cache</h3>
                  <div className="command-buttons">
                    <button 
                      className="command-btn"
                      onClick={() => runSystemCommand('clear_cache')}
                    >
                      Clear Cache
                    </button>
                    <button 
                      className="command-btn"
                      onClick={() => runSystemCommand('warm_cache')}
                    >
                      Warm Cache
                    </button>
                  </div>
                </div>

                <div className="command-card">
                  <h3>📧 Notifications</h3>
                  <div className="command-buttons">
                    <button 
                      className="command-btn"
                      onClick={() => runSystemCommand('send_newsletter')}
                    >
                      Send Newsletter
                    </button>
                    <button 
                      className="command-btn"
                      onClick={() => runSystemCommand('notify_maintenance')}
                    >
                      Maintenance Alert
                    </button>
                  </div>
                </div>

                <div className="command-card">
                  <h3>🔧 Maintenance</h3>
                  <div className="command-buttons">
                    <button 
                      className="command-btn danger"
                      onClick={() => runSystemCommand('maintenance_mode')}
                    >
                      Maintenance Mode
                    </button>
                    <button
                      className="command-btn"
                      onClick={() => runSystemCommand('health_check')}
                    >
                      System Health Check
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Service controls. Deliberately a separate section ABOVE the danger
                zone, not a milder button inside it: the whole value of the danger
                zone is the line it draws, and the routine tool sitting among the
                break-glass ones is what rubs that line out. This is the button
                that fixes the common failure — a wedged MUD on a healthy box. */}
            {rebootTargets.some(t => t.kind === 'service') && (
              <div className="system-section" style={{ marginTop: '30px' }}>
                <h2 style={{ color: '#ffd43b' }}>🔄 Service Controls</h2>
                <p style={{ color: '#aaa', marginTop: '-8px', marginBottom: '18px' }}>
                  Try this first. If the realm is frozen, unresponsive, or behaving strangely
                  but the website and this admin page still work, restarting the MUD is almost
                  always the right fix — and it's back in under a minute.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {rebootTargets.filter(t => t.kind === 'service').map(target => (
                    <div
                      key={target.target}
                      style={{
                        border: '1px solid #8a7320', borderRadius: '8px', padding: '16px',
                        backgroundColor: '#1e1e1e', display: 'flex', gap: '16px',
                        alignItems: 'center', flexWrap: 'wrap'
                      }}
                    >
                      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                        <div style={{ fontWeight: 'bold', color: '#fff', marginBottom: '4px' }}>
                          🔄 Restart {target.label}
                          <span style={{ color: '#888', fontWeight: 'normal', marginLeft: '8px', fontSize: '13px' }}>
                            {target.ref} · systemctl
                          </span>
                        </div>
                        <div style={{ color: '#aaa', fontSize: '14px' }}>{target.description}</div>
                        {/* Surfaced on the card, not only in the dialog: whoever
                            staged the build is usually not whoever presses this. */}
                        {target.stagedBinary && (
                          <div style={{ color: '#ffd43b', fontSize: '13px', marginTop: '6px' }}>
                            ⚠️ A new binary is staged — restarting will also deploy it.
                          </div>
                        )}
                      </div>
                      <button
                        className="command-btn"
                        onClick={() => openRebootModal(target)}
                        style={{ backgroundColor: '#a67c00', color: '#fff' }}
                      >
                        Restart {target.label}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Danger zone: whole-machine reboots. Deliberately last on the page,
                visually separated, and never one click from firing. */}
            <div className="system-section" style={{ marginTop: '30px' }}>
              <h2 style={{ color: '#ff6b6b' }}>⚠️ Danger Zone</h2>
              <p style={{ color: '#aaa', marginTop: '-8px', marginBottom: '18px' }}>
                These reboot an entire machine, not a service. If the MUD is misbehaving but the
                box is healthy, use <strong>Restart the MUD</strong> above instead — a reboot takes
                the website, the API and everything else down with it, and is the last resort.
              </p>

              {rebootTargets.filter(t => t.kind !== 'service').length === 0 ? (
                <p style={{ color: '#aaa' }}>No reboot targets configured for this server.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {rebootTargets.filter(t => t.kind !== 'service').map(target => (
                    <div
                      key={target.target}
                      style={{
                        border: `1px solid ${target.isProduction ? '#ff2d2d' : '#666'}`,
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: '#1e1e1e',
                        display: 'flex',
                        gap: '16px',
                        alignItems: 'center',
                        flexWrap: 'wrap'
                      }}
                    >
                      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                        <div style={{ fontWeight: 'bold', color: '#fff', marginBottom: '4px' }}>
                          {target.target === 'prod' ? '☁️' : '📍'} {target.label}
                          {/* Called out in the list, not just the modal: on prod the
                              LOCAL button is the production one, and the label is the
                              only thing distinguishing them at a glance. */}
                          {target.isProduction && (
                            <span style={{
                              backgroundColor: '#ff2d2d', color: '#fff', fontSize: '11px',
                              fontWeight: 'bold', padding: '2px 7px', borderRadius: '4px',
                              marginLeft: '8px', letterSpacing: '0.5px', verticalAlign: 'middle'
                            }}>
                              PRODUCTION
                            </span>
                          )}
                          <span style={{ color: '#888', fontWeight: 'normal', marginLeft: '8px', fontSize: '13px' }}>
                            {target.kind === 'ec2' ? `${target.ref} · ${target.region} · AWS EC2 API` : 'this machine · systemctl'}
                          </span>
                        </div>
                        <div style={{ color: '#aaa', fontSize: '14px' }}>{target.description}</div>
                      </div>
                      <button
                        className="command-btn danger"
                        onClick={() => openRebootModal(target)}
                      >
                        Reboot {target.label}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {rebootLog.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ color: '#aaa', fontSize: '15px' }}>Recent restarts &amp; reboots</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', color: '#ccc' }}>
                      <thead>
                        <tr style={{ textAlign: 'left', color: '#888' }}>
                          <th style={{ padding: '6px 10px 6px 0' }}>When</th>
                          <th style={{ padding: '6px 10px 6px 0' }}>Target</th>
                          <th style={{ padding: '6px 10px 6px 0' }}>By</th>
                          <th style={{ padding: '6px 10px 6px 0' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rebootLog.map(entry => (
                          <tr key={entry.id} style={{ borderTop: '1px solid #333' }}>
                            <td style={{ padding: '6px 10px 6px 0' }}>{new Date(entry.requested_at).toLocaleString()}</td>
                            <td style={{ padding: '6px 10px 6px 0' }}>{entry.target_ref}</td>
                            <td style={{ padding: '6px 10px 6px 0' }}>{entry.requested_by_email || '—'}</td>
                            <td style={{ padding: '6px 10px 6px 0' }}>
                              <span style={{
                                color: entry.status === 'failed' ? '#ff6b6b'
                                  : entry.status === 'ok' ? '#4ade80'
                                  : entry.status === 'degraded' ? '#ffd43b' : '#888'
                              }}>
                                {entry.status}
                              </span>
                              {entry.error && (
                                <span style={{ color: '#ff6b6b', marginLeft: '8px' }}>{entry.error}</span>
                              )}
                              {/* e.g. "this restart promoted a staged binary" --
                                  the reason the realm may behave differently now. */}
                              {entry.notes && (
                                <span style={{ color: '#ffd43b', marginLeft: '8px' }}>{entry.notes}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* 'dispatching' is the resting state for a local reboot, not a stuck job:
                      the process that would have written the outcome was killed by it. */}
                  <p style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>
                    A local reboot stays at <code>dispatching</code> — the API is killed by the reboot it ordered,
                    so it never gets to record the outcome.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Break-glass confirmation.
            Three deliberate acts are required, each guarding a different mistake:
              1. reading which machine this is  -- guards "I thought I was on dev"
              2. acknowledging the human check  -- guards "nobody knew this was happening"
              3. typing the machine's name      -- guards the reflexive click
            The typed phrase is enforced server-side too; the disabled button is a
            courtesy, never the actual gate. */}
        {pendingReboot && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000, padding: '20px', overflowY: 'auto'
          }}>
            <div style={{
              backgroundColor: '#141414',
              border: `3px solid ${pendingReboot.kind === 'service' ? '#a67c00'
                : pendingReboot.isProduction ? '#ff2d2d' : '#ff9f43'}`,
              color: '#eee',
              borderRadius: '12px',
              maxWidth: '680px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 0 60px rgba(0,0,0,0.9)'
            }}>
              {/* Banner: severity is the machine's, not the button's. The service
                  restart gets a visibly different, calmer banner -- if it looked
                  like the reboot dialog, staff would learn to click through the
                  reboot dialog. */}
              <div style={{
                backgroundColor: pendingReboot.kind === 'service' ? '#e0a800'
                  : pendingReboot.isProduction ? '#ff2d2d' : '#ff9f43',
                color: pendingReboot.kind === 'service' ? '#241a00'
                  : pendingReboot.isProduction ? '#fff' : '#241a00',
                padding: '14px 28px',
                borderRadius: '9px 9px 0 0',
                fontWeight: 'bold',
                fontSize: '17px',
                letterSpacing: '0.5px',
                textAlign: 'center'
              }}>
                {pendingReboot.kind === 'service'
                  ? `🔄 RESTART SERVICE${pendingReboot.isProduction ? ' — PRODUCTION' : ''}`
                  : `🚨 BREAK GLASS — ${pendingReboot.isProduction ? 'PRODUCTION' : 'NON-PRODUCTION'} MACHINE REBOOT`}
              </div>

              <div style={{ padding: '26px 28px 28px' }}>
                {/* The machine name, unmissable. The single most common way to get
                    this wrong is being certain you are on the other box. */}
                <div style={{ textAlign: 'center', marginBottom: '22px' }}>
                  <div style={{ color: '#888', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                    You are about to {pendingReboot.kind === 'service' ? 'restart' : 'reboot'}
                  </div>
                  <div style={{
                    fontSize: '42px',
                    fontWeight: 'bold',
                    fontFamily: 'monospace',
                    color: pendingReboot.kind === 'service' ? '#ffd43b'
                      : pendingReboot.isProduction ? '#ff6b6b' : '#ffd43b',
                    lineHeight: 1.2,
                    margin: '6px 0',
                    wordBreak: 'break-all'
                  }}>
                    {pendingReboot.label}
                  </div>
                  <div style={{ color: '#888', fontSize: '13px', fontFamily: 'monospace' }}>
                    {pendingReboot.ref}
                    {pendingReboot.region ? ` · ${pendingReboot.region}` : ''}
                    {' · '}
                    {pendingReboot.kind === 'ec2' ? 'via AWS EC2 API'
                      : pendingReboot.kind === 'service' ? 'via systemctl on this machine'
                      : 'via systemctl on this machine'}
                  </div>
                </div>

                {/* The staged-binary warning. This is the one thing in the dialog
                    the presser cannot possibly know from context: a restart is
                    also a deploy whenever someone has left a build staged. */}
                {pendingReboot.kind === 'service' && pendingReboot.stagedBinary && (
                  <div style={{
                    backgroundColor: '#2a2410', border: '1px solid #8a7320',
                    borderRadius: '8px', padding: '16px 18px', marginBottom: '18px'
                  }}>
                    <div style={{ color: '#ffd43b', fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                      ⚠️ This restart will also deploy a new binary
                    </div>
                    <p style={{ margin: '0 0 8px', color: '#ccc', fontSize: '14px', lineHeight: 1.6 }}>
                      A build is staged at <code style={{ color: '#ffd43b' }}>{pendingReboot.stagedBinary.path}</code>{' '}
                      ({pendingReboot.stagedBinary.bytes.toLocaleString()} bytes, staged{' '}
                      {new Date(pendingReboot.stagedBinary.mtime).toLocaleString()}). Starting the
                      service promotes it over the running one, so the realm will come back on a
                      <em> different build</em> than it is running now.
                    </p>
                    <p style={{ margin: 0, color: '#ccc', fontSize: '14px', lineHeight: 1.6 }}>
                      If you weren't expecting that, stop and check with {pendingReboot.contactName} first.
                    </p>
                  </div>
                )}

                <div style={{
                  backgroundColor: '#1e1e1e', border: '1px solid #333',
                  borderRadius: '8px', padding: '16px 18px', marginBottom: '18px'
                }}>
                  <div style={{
                    color: pendingReboot.kind === 'service' ? '#ffd43b' : '#ff6b6b',
                    fontWeight: 'bold', marginBottom: '10px', fontSize: '14px'
                  }}>
                    What happens when you press this
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#ccc', fontSize: '14px', lineHeight: 1.7 }}>
                    {pendingReboot.impact.map((line, i) => <li key={i}>{line}</li>)}
                    {pendingReboot.kind !== 'service' && (
                      <li>Recovery takes roughly 1–2 minutes if the machine comes back cleanly</li>
                    )}
                  </ul>
                </div>

                {/* The point of the whole dialog: this is not a maintenance tool.
                    Shown only for the machine reboots -- the service restart IS a
                    maintenance tool, and telling staff otherwise would push them
                    back toward the reboot buttons this exists to keep them off. */}
                {pendingReboot.requiresPhrase && (
                <div style={{
                  backgroundColor: '#2a1a1a', border: '1px solid #6b2d2d',
                  borderRadius: '8px', padding: '16px 18px', marginBottom: '18px'
                }}>
                  <div style={{ color: '#ffd43b', fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                    ⚠️ This is a last resort, not a fix
                  </div>
                  <p style={{ margin: '0 0 10px', color: '#ccc', fontSize: '14px', lineHeight: 1.6 }}>
                    Rebooting the machine is the biggest hammer available and it loses anything
                    in memory that hasn't been written to disk. If a single service is
                    misbehaving, restart <em>that service</em> instead — a reboot fixes fewer
                    things than people expect, and takes everything else down with it.
                  </p>
                  <p style={{ margin: 0, color: '#ccc', fontSize: '14px', lineHeight: 1.6 }}>
                    <strong style={{ color: '#fff' }}>
                      Contact {pendingReboot.contactName} before you do this.
                    </strong>{' '}
                    {pendingReboot.contactHow
                      ? pendingReboot.contactHow
                      : `If ${pendingReboot.contactName} can be reached at all, they should make this call.`}
                    {' '}Press on alone only if it genuinely cannot wait.
                  </p>
                </div>
                )}

                {pendingReboot.requiresPhrase && (
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  cursor: 'pointer', marginBottom: '18px', color: '#ddd', fontSize: '14px', lineHeight: 1.5
                }}>
                  <input
                    type="checkbox"
                    checked={rebootAckContact}
                    onChange={(e) => setRebootAckContact(e.target.checked)}
                    style={{ marginTop: '3px', width: '17px', height: '17px', flexShrink: 0, cursor: 'pointer' }}
                  />
                  <span>
                    I've tried to reach {pendingReboot.contactName}, or this is an emergency that
                    can't wait — and I've confirmed restarting an individual service won't fix it.
                  </span>
                </label>
                )}

                {pendingReboot.requiresPhrase && (
                  <>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: '#aaa' }}>
                      Type <code style={{ color: '#ffd43b', fontSize: '15px' }}>{pendingReboot.confirmPhrase}</code> to unlock:
                    </label>
                    <input
                      type="text"
                      value={rebootConfirmText}
                      onChange={(e) => setRebootConfirmText(e.target.value)}
                      autoFocus
                      spellCheck={false}
                      autoComplete="off"
                      placeholder={pendingReboot.confirmPhrase}
                      style={{
                        width: '100%', padding: '12px', fontFamily: 'monospace', fontSize: '16px',
                        backgroundColor: '#0a0a0a', color: '#fff',
                        border: `1px solid ${rebootConfirmText.trim().toLowerCase() === pendingReboot.confirmPhrase ? '#4ade80' : '#555'}`,
                        borderRadius: '5px', boxSizing: 'border-box'
                      }}
                    />
                  </>
                )}

                {rebootError && (
                  <p style={{
                    color: '#ff6b6b', marginTop: '14px', marginBottom: 0, fontSize: '14px',
                    backgroundColor: '#2a1414', border: '1px solid #6b2d2d',
                    borderRadius: '6px', padding: '10px 12px'
                  }}>
                    {rebootError}
                  </p>
                )}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={closeRebootModal}
                    disabled={rebootInFlight}
                    style={{
                      padding: '13px 26px', backgroundColor: '#3a3a3a', color: '#eee',
                      border: 'none', borderRadius: '6px', cursor: 'pointer',
                      fontSize: '15px', fontWeight: 'bold'
                    }}
                  >
                    Cancel — don't {pendingReboot.kind === 'service' ? 'restart' : 'reboot'}
                  </button>
                  <button
                    type="button"
                    onClick={confirmReboot}
                    disabled={
                      rebootInFlight ||
                      (pendingReboot.requiresPhrase && (
                        !rebootAckContact ||
                        rebootConfirmText.trim().toLowerCase() !== pendingReboot.confirmPhrase
                      ))
                    }
                    style={{
                      padding: '13px 26px',
                      backgroundColor: !pendingReboot.requiresPhrase ? '#a67c00'
                        : rebootAckContact && rebootConfirmText.trim().toLowerCase() === pendingReboot.confirmPhrase
                          ? '#c92a2a' : '#4a2626',
                      color: !pendingReboot.requiresPhrase ? '#fff'
                        : rebootAckContact && rebootConfirmText.trim().toLowerCase() === pendingReboot.confirmPhrase
                          ? '#fff' : '#8a7070',
                      border: 'none', borderRadius: '6px', fontSize: '15px', fontWeight: 'bold',
                      cursor: rebootInFlight ? 'wait' : 'pointer',
                      opacity: rebootInFlight ? 0.7 : 1
                    }}
                  >
                    {/* The restart waits for the realm to answer before returning,
                        so this button stays busy for up to a minute. Say so. */}
                    {rebootInFlight
                      ? (pendingReboot.kind === 'service' ? 'Restarting — waiting for the realm…' : 'Rebooting…')
                      : pendingReboot.kind === 'service'
                        ? `Restart ${pendingReboot.label} now`
                        : `Reboot ${pendingReboot.label} now`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'loot' && (
          <div className="admin-content">
            {/* Lootbox Events Panel */}
            <div style={{ backgroundColor: '#1e1e1e', border: '1px solid #ffd43b44', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
              <h2 style={{ color: '#ffd43b', marginTop: 0, marginBottom: '16px' }}>🎁 Lootbox Events</h2>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '4px' }}>Start Date</label>
                  <input
                    type="datetime-local"
                    value={lootAnalyticsParams.startDate}
                    onChange={(e) => setLootAnalyticsParams({ ...lootAnalyticsParams, startDate: e.target.value, page: 1 })}
                    style={{ padding: '6px 10px', backgroundColor: '#2a2a2a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '4px' }}>End Date</label>
                  <input
                    type="datetime-local"
                    value={lootAnalyticsParams.endDate}
                    onChange={(e) => setLootAnalyticsParams({ ...lootAnalyticsParams, endDate: e.target.value, page: 1 })}
                    style={{ padding: '6px 10px', backgroundColor: '#2a2a2a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '4px' }}>Type</label>
                  <select
                    value={lootAnalyticsParams.eventType}
                    onChange={(e) => setLootAnalyticsParams({ ...lootAnalyticsParams, eventType: e.target.value, page: 1 })}
                    style={{ padding: '6px 10px', backgroundColor: '#2a2a2a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
                  >
                    <option value="">All</option>
                    <option value="5">Won (5)</option>
                    <option value="6">Delivered (6)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '4px' }}>Player ID</label>
                  <input
                    type="text"
                    value={lootAnalyticsParams.playerId}
                    onChange={(e) => setLootAnalyticsParams({ ...lootAnalyticsParams, playerId: e.target.value, page: 1 })}
                    placeholder="Any"
                    style={{ padding: '6px 10px', backgroundColor: '#2a2a2a', color: '#fff', border: '1px solid #555', borderRadius: '4px', width: '80px' }}
                  />
                </div>
                <button
                  onClick={() => fetchLootAnalytics(lootAnalyticsParams)}
                  disabled={lootAnalyticsLoading}
                  style={{ padding: '6px 16px', backgroundColor: '#ffd43b', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  {lootAnalyticsLoading ? 'Loading...' : 'Update'}
                </button>
              </div>

              {lootAnalyticsData && (
                <>
                  {/* Summary cards */}
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    {[
                      { label: 'Total', value: lootAnalyticsData.summary.total_events, color: '#fff' },
                      { label: 'Won', value: lootAnalyticsData.summary.lootbox_won_count, color: '#ffd43b' },
                      { label: 'Delivered', value: lootAnalyticsData.summary.loot_delivered_count, color: '#90EE90' },
                      { label: 'Players', value: lootAnalyticsData.summary.unique_players, color: '#4dabf7' },
                      { label: 'Common', value: lootAnalyticsData.summary.common_count, color: '#aaa' },
                      { label: 'Rare', value: lootAnalyticsData.summary.rare_count, color: '#4dabf7' },
                      { label: 'Epic', value: lootAnalyticsData.summary.epic_count, color: '#DDA0DD' },
                      { label: 'Legendary', value: lootAnalyticsData.summary.legendary_count, color: '#ffd43b' },
                    ].map((card) => (
                      <div key={card.label} style={{ backgroundColor: '#2a2a2a', padding: '10px 16px', borderRadius: '6px', textAlign: 'center', minWidth: '80px' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: card.color }}>{Number(card.value).toLocaleString()}</div>
                        <div style={{ fontSize: '11px', color: '#888' }}>{card.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Events table */}
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #444' }}>
                          {[
                            { key: 'created_at', label: 'Time' },
                            { key: 'event_id', label: 'Type' },
                            { key: 'data1', label: 'Player' },
                            { key: 'string1', label: 'Name/Lvl' },
                            { key: 'data3', label: 'Rarity/Box' },
                            { key: 'data4', label: 'Data4' },
                            { key: 'data5', label: 'Location' },
                          ].map((col) => (
                            <th
                              key={col.key}
                              onClick={() => handleLootSort(col.key)}
                              style={{ padding: '8px', textAlign: 'left', cursor: 'pointer', color: '#aaa', userSelect: 'none', whiteSpace: 'nowrap' }}
                            >
                              {col.label} {lootAnalyticsParams.sortBy === col.key && (lootAnalyticsParams.sortDir === 'desc' ? '▼' : '▲')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {lootAnalyticsData.events.map((event) => (
                          <tr key={event.id} style={{ borderBottom: '1px solid #333' }}>
                            <td style={{ padding: '6px 8px', color: '#ccc', whiteSpace: 'nowrap' }}>
                              {new Date(event.created_at).toLocaleString()}
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <span style={{ color: event.event_id === 5 ? '#ffd43b' : '#90EE90' }}>
                                {event.event_id === 5 ? 'Won' : 'Delivered'}
                              </span>
                            </td>
                            <td style={{ padding: '6px 8px', color: '#ccc' }}>{event.character_id}</td>
                            <td style={{ padding: '6px 8px', color: '#ccc' }}>{event.string1}</td>
                            <td style={{ padding: '6px 8px' }}>
                              {event.event_id === 5 ? (
                                <span style={{ color: getRarityColor(event.data3) }}>{getRarityName(event.data3)}</span>
                              ) : (
                                <span style={{ color: '#ccc' }}>{event.data3}</span>
                              )}
                            </td>
                            <td style={{ padding: '6px 8px', color: '#ccc' }}>{event.data4}</td>
                            <td style={{ padding: '6px 8px', color: '#888' }}>{event.location || '—'}</td>
                          </tr>
                        ))}
                        {lootAnalyticsData.events.length === 0 && (
                          <tr><td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No lootbox events found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {lootAnalyticsData.pagination.totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                      <button onClick={() => handleLootPageChange(1)} disabled={lootAnalyticsData.pagination.page === 1}
                        style={{ padding: '4px 10px', backgroundColor: '#333', color: lootAnalyticsData.pagination.page === 1 ? '#666' : '#fff', border: '1px solid #555', borderRadius: '4px', cursor: 'pointer' }}>«</button>
                      <button onClick={() => handleLootPageChange(lootAnalyticsData.pagination.page - 1)} disabled={lootAnalyticsData.pagination.page === 1}
                        style={{ padding: '4px 10px', backgroundColor: '#333', color: lootAnalyticsData.pagination.page === 1 ? '#666' : '#fff', border: '1px solid #555', borderRadius: '4px', cursor: 'pointer' }}>‹</button>
                      <span style={{ color: '#ccc', fontSize: '13px' }}>Page {lootAnalyticsData.pagination.page} of {lootAnalyticsData.pagination.totalPages}</span>
                      <button onClick={() => handleLootPageChange(lootAnalyticsData.pagination.page + 1)} disabled={lootAnalyticsData.pagination.page === lootAnalyticsData.pagination.totalPages}
                        style={{ padding: '4px 10px', backgroundColor: '#333', color: lootAnalyticsData.pagination.page === lootAnalyticsData.pagination.totalPages ? '#666' : '#fff', border: '1px solid #555', borderRadius: '4px', cursor: 'pointer' }}>›</button>
                      <button onClick={() => handleLootPageChange(lootAnalyticsData.pagination.totalPages)} disabled={lootAnalyticsData.pagination.page === lootAnalyticsData.pagination.totalPages}
                        style={{ padding: '4px 10px', backgroundColor: '#333', color: lootAnalyticsData.pagination.page === lootAnalyticsData.pagination.totalPages ? '#666' : '#fff', border: '1px solid #555', borderRadius: '4px', cursor: 'pointer' }}>»</button>
                    </div>
                  )}
                </>
              )}

              {lootAnalyticsLoading && !lootAnalyticsData && (
                <p style={{ color: '#888', textAlign: 'center' }}>Loading lootbox events...</p>
              )}
            </div>

            {/* Item Events Panel */}
            <div style={{ backgroundColor: '#1e1e1e', border: '1px solid #90EE9044', borderRadius: '8px', padding: '20px' }}>
              <h2 style={{ color: '#90EE90', marginTop: 0, marginBottom: '16px' }}>📦 Item Events</h2>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '4px' }}>Start Date</label>
                  <input
                    type="datetime-local"
                    value={itemAnalyticsParams.startDate}
                    onChange={(e) => setItemAnalyticsParams({ ...itemAnalyticsParams, startDate: e.target.value, page: 1 })}
                    style={{ padding: '6px 10px', backgroundColor: '#2a2a2a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '4px' }}>End Date</label>
                  <input
                    type="datetime-local"
                    value={itemAnalyticsParams.endDate}
                    onChange={(e) => setItemAnalyticsParams({ ...itemAnalyticsParams, endDate: e.target.value, page: 1 })}
                    style={{ padding: '6px 10px', backgroundColor: '#2a2a2a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '4px' }}>Type</label>
                  <select
                    value={itemAnalyticsParams.eventType}
                    onChange={(e) => setItemAnalyticsParams({ ...itemAnalyticsParams, eventType: e.target.value, page: 1 })}
                    style={{ padding: '6px 10px', backgroundColor: '#2a2a2a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
                  >
                    <option value="">All</option>
                    <option value="7">Received (7)</option>
                    <option value="8">Queue (8)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '4px' }}>Player ID</label>
                  <input
                    type="text"
                    value={itemAnalyticsParams.playerId}
                    onChange={(e) => setItemAnalyticsParams({ ...itemAnalyticsParams, playerId: e.target.value, page: 1 })}
                    placeholder="Any"
                    style={{ padding: '6px 10px', backgroundColor: '#2a2a2a', color: '#fff', border: '1px solid #555', borderRadius: '4px', width: '80px' }}
                  />
                </div>
                <button
                  onClick={() => fetchItemAnalytics(itemAnalyticsParams)}
                  disabled={itemAnalyticsLoading}
                  style={{ padding: '6px 16px', backgroundColor: '#90EE90', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  {itemAnalyticsLoading ? 'Loading...' : 'Update'}
                </button>
              </div>

              {itemAnalyticsData && (
                <>
                  {/* Summary cards */}
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    {[
                      { label: 'Total', value: itemAnalyticsData.summary.total_events, color: '#fff' },
                      { label: 'Received', value: itemAnalyticsData.summary.items_received_count, color: '#90EE90' },
                      { label: 'Queue', value: itemAnalyticsData.summary.queue_items_count, color: '#ffd43b' },
                      { label: 'Players', value: itemAnalyticsData.summary.unique_players, color: '#4dabf7' },
                      { label: 'Total Items', value: itemAnalyticsData.summary.total_items, color: '#fff' },
                      { label: 'Lootboxes', value: itemAnalyticsData.summary.lootbox_drops_count, color: '#ff8787' },
                    ].map((card) => (
                      <div key={card.label} style={{ backgroundColor: '#2a2a2a', padding: '10px 16px', borderRadius: '6px', textAlign: 'center', minWidth: '80px' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: card.color }}>{Number(card.value).toLocaleString()}</div>
                        <div style={{ fontSize: '11px', color: '#888' }}>{card.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Events table */}
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #444' }}>
                          {[
                            { key: 'created_at', label: 'Time' },
                            { key: 'event_id', label: 'Type' },
                            { key: 'data1', label: 'Player' },
                            { key: 'data2', label: 'Customer' },
                            { key: 'data3', label: 'Item #' },
                            { key: 'data4', label: 'Qty' },
                            { key: 'data5', label: 'Location' },
                          ].map((col) => (
                            <th
                              key={col.key}
                              onClick={() => handleItemSort(col.key)}
                              style={{ padding: '8px', textAlign: 'left', cursor: 'pointer', color: '#aaa', userSelect: 'none', whiteSpace: 'nowrap' }}
                            >
                              {col.label} {itemAnalyticsParams.sortBy === col.key && (itemAnalyticsParams.sortDir === 'desc' ? '▼' : '▲')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {itemAnalyticsData.events.map((event) => (
                          <tr key={event.id} style={{ borderBottom: '1px solid #333' }}>
                            <td style={{ padding: '6px 8px', color: '#ccc', whiteSpace: 'nowrap' }}>
                              {new Date(event.created_at).toLocaleString()}
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <span style={{ color: event.event_id === 7 ? '#90EE90' : '#ffd43b' }}>
                                {event.event_id === 7 ? 'Received' : 'Queue'}
                              </span>
                            </td>
                            <td style={{ padding: '6px 8px', color: '#ccc' }}>{event.character_id}</td>
                            <td style={{ padding: '6px 8px', color: '#ccc' }}>{event.customer_id}</td>
                            <td style={{ padding: '6px 8px', color: '#ccc' }}>{event.item_id}</td>
                            <td style={{ padding: '6px 8px', color: '#ccc' }}>{event.quantity}</td>
                            <td style={{ padding: '6px 8px', color: '#888' }}>{event.location || '—'}</td>
                          </tr>
                        ))}
                        {itemAnalyticsData.events.length === 0 && (
                          <tr><td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No item events found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {itemAnalyticsData.pagination.totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                      <button onClick={() => handleItemPageChange(1)} disabled={itemAnalyticsData.pagination.page === 1}
                        style={{ padding: '4px 10px', backgroundColor: '#333', color: itemAnalyticsData.pagination.page === 1 ? '#666' : '#fff', border: '1px solid #555', borderRadius: '4px', cursor: 'pointer' }}>«</button>
                      <button onClick={() => handleItemPageChange(itemAnalyticsData.pagination.page - 1)} disabled={itemAnalyticsData.pagination.page === 1}
                        style={{ padding: '4px 10px', backgroundColor: '#333', color: itemAnalyticsData.pagination.page === 1 ? '#666' : '#fff', border: '1px solid #555', borderRadius: '4px', cursor: 'pointer' }}>‹</button>
                      <span style={{ color: '#ccc', fontSize: '13px' }}>Page {itemAnalyticsData.pagination.page} of {itemAnalyticsData.pagination.totalPages}</span>
                      <button onClick={() => handleItemPageChange(itemAnalyticsData.pagination.page + 1)} disabled={itemAnalyticsData.pagination.page === itemAnalyticsData.pagination.totalPages}
                        style={{ padding: '4px 10px', backgroundColor: '#333', color: itemAnalyticsData.pagination.page === itemAnalyticsData.pagination.totalPages ? '#666' : '#fff', border: '1px solid #555', borderRadius: '4px', cursor: 'pointer' }}>›</button>
                      <button onClick={() => handleItemPageChange(itemAnalyticsData.pagination.totalPages)} disabled={itemAnalyticsData.pagination.page === itemAnalyticsData.pagination.totalPages}
                        style={{ padding: '4px 10px', backgroundColor: '#333', color: itemAnalyticsData.pagination.page === itemAnalyticsData.pagination.totalPages ? '#666' : '#fff', border: '1px solid #555', borderRadius: '4px', cursor: 'pointer' }}>»</button>
                    </div>
                  )}
                </>
              )}

              {itemAnalyticsLoading && !itemAnalyticsData && (
                <p style={{ color: '#888', textAlign: 'center' }}>Loading item events...</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="admin-content">
            <div className="system-section">
              <h3>System Settings</h3>
              <p style={{ color: '#888', marginBottom: '20px' }}>
                Configure system-wide settings for the Majic Realm platform.
              </p>

              {settingsLoading ? (
                <p>Loading settings...</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {systemSettings.map((setting) => (
                    <div
                      key={setting.key}
                      style={{
                        backgroundColor: '#2a2a2a',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid #404040'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <h4 style={{ margin: '0 0 8px 0', color: '#fff' }}>
                            {setting.key === 'skip_email_verification' ? 'Skip Email Verification' : setting.key}
                          </h4>
                          <p style={{ margin: 0, color: '#888', fontSize: '14px' }}>
                            {setting.description}
                          </p>
                          <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '12px' }}>
                            Last updated: {new Date(setting.updated_at).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          {setting.key === 'skip_email_verification' ? (
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={setting.value === 'true'}
                                onChange={(e) => updateSetting(setting.key, e.target.checked ? 'true' : 'false')}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  marginRight: '10px',
                                  cursor: 'pointer'
                                }}
                              />
                              <span style={{
                                color: setting.value === 'true' ? '#4CAF50' : '#f44336',
                                fontWeight: 'bold'
                              }}>
                                {setting.value === 'true' ? 'ENABLED' : 'DISABLED'}
                              </span>
                            </label>
                          ) : (
                            <input
                              type="text"
                              value={setting.value}
                              onChange={(e) => updateSetting(setting.key, e.target.value)}
                              style={{
                                padding: '8px 12px',
                                backgroundColor: '#333',
                                border: '1px solid #555',
                                borderRadius: '4px',
                                color: '#fff'
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {systemSettings.length === 0 && (
                    <p style={{ color: '#888' }}>No settings found.</p>
                  )}

                  <div style={{
                    backgroundColor: '#1a1a1a',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #333',
                    marginTop: '10px'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#ffc107' }}>Warning</h4>
                    <p style={{ margin: 0, color: '#888', fontSize: '14px' }}>
                      When "Skip Email Verification" is enabled, new accounts will be created immediately without
                      requiring email verification. This is useful for testing but should be disabled in production.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Script Modal */}
        {showScriptModal && selectedUserForScript && (
          <div className="product-form-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div className="product-form-container" style={{
              backgroundColor: 'white',
              color: '#333',
              padding: '30px',
              borderRadius: '10px',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto',
              width: '90%'
            }}>
              <h3>🎁 Post Script to Realm</h3>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Posting script to <strong>{selectedUserForScript.username || selectedUserForScript.email}</strong> (Realm ID: {selectedUserForScript.legacy_realm_id})
              </p>
              <form onSubmit={handlePostScript}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Script *
                  </label>
                  <textarea
                    value={scriptInput}
                    onChange={(e) => setScriptInput(e.target.value)}
                    rows={4}
                    required
                    placeholder="e.g., 2;587;1;0;0;0;@ # add a guitar"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      fontSize: '14px'
                    }}
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                    Example: 2;587;1;0;0;0;@ # add a guitar
                  </small>
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowScriptModal(false);
                      setSelectedUserForScript(null);
                      setScriptInput('');
                    }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#ccc',
                      color: '#333',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Post
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditUserModal && editingUser && (
          <div className="product-form-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div className="product-form-container" style={{
              backgroundColor: 'white',
              color: '#333',
              padding: '30px',
              borderRadius: '10px',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto',
              width: '90%'
            }}>
              <h3>Edit User</h3>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Editing user ID: <strong>{editingUser.id}</strong>
              </p>
              <form onSubmit={handleEditUserSubmit} autoComplete="off">
                {/* Hidden fields to trick password managers */}
                <input type="text" name="fake_user" style={{ display: 'none' }} tabIndex={-1} autoComplete="username" />
                <input type="password" name="fake_pass" style={{ display: 'none' }} tabIndex={-1} autoComplete="current-password" />

                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      First Name
                    </label>
                    <input
                      type="text"
                      name="admin_edit_fname"
                      value={editUserForm.first_name}
                      onChange={(e) => setEditUserForm({ ...editUserForm, first_name: e.target.value })}
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="admin_edit_lname"
                      value={editUserForm.last_name}
                      onChange={(e) => setEditUserForm({ ...editUserForm, last_name: e.target.value })}
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Username
                  </label>
                  <input
                    type="text"
                    name="admin_edit_uname"
                    value={editUserForm.username}
                    onChange={(e) => setEditUserForm({ ...editUserForm, username: e.target.value })}
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Email *
                  </label>
                  <input
                    type="text"
                    name="admin_edit_mail"
                    value={editUserForm.email}
                    onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                    required
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    New Password
                    <span style={{ fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>(leave blank to keep current)</span>
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type={showEditPassword ? 'text' : 'password'}
                      name="admin_edit_pw"
                      value={editUserForm.password}
                      onChange={(e) => setEditUserForm({ ...editUserForm, password: e.target.value })}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      style={{
                        flex: 1,
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowEditPassword(!showEditPassword)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#f0f0f0',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title={showEditPassword ? 'Hide password' : 'Show password'}
                    >
                      {showEditPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Referral Code
                  </label>
                  <input
                    type="text"
                    name="admin_edit_referral"
                    value={editUserForm.referral_code}
                    onChange={(e) => setEditUserForm({ ...editUserForm, referral_code: e.target.value })}
                    placeholder="Enter referral code"
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Legacy Realm ID
                  </label>
                  <input
                    type="number"
                    name="admin_edit_realm_id"
                    value={editUserForm.legacy_realm_id}
                    onChange={(e) => setEditUserForm({ ...editUserForm, legacy_realm_id: e.target.value })}
                    placeholder="No realm ID assigned"
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <small style={{ color: '#666', fontSize: '11px' }}>
                    The MUD game account ID. Leave blank to clear.
                  </small>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#c00' }}>
                    Subscription Override (Staff)
                  </label>
                  <select
                    name="admin_edit_sub_override"
                    value={editUserForm.subscription_override}
                    onChange={(e) => setEditUserForm({ ...editUserForm, subscription_override: parseInt(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxSizing: 'border-box',
                      backgroundColor: editUserForm.subscription_override > 0 ? '#ffe0e0' : 'white'
                    }}
                  >
                    <option value={0}>None (Normal User)</option>
                    <option value={1}>Platinum</option>
                    <option value={2}>Gold</option>
                    <option value={3}>Silver</option>
                    <option value={4}>Copper</option>
                    <option value={5}>NoSub (Test - Force Zero)</option>
                  </select>
                  <small style={{ color: '#666', fontSize: '11px' }}>
                    Override bypasses Stripe - use for staff/NPC accounts. NoSub forces uwSubType=0 for testing.
                  </small>
                </div>

                {/* Stripe Customer Section */}
                <div style={{
                  marginTop: '15px',
                  marginBottom: '15px',
                  padding: '15px',
                  backgroundColor: '#f0f7ff',
                  borderRadius: '8px',
                  border: '1px solid #cce0ff'
                }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                    Stripe Customer
                  </label>
                  {editingUser?.stripe_customer_id ? (
                    <div>
                      <span style={{ color: '#28a745', fontWeight: 'bold' }}>&#10003;</span>{' '}
                      <code style={{ backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px', fontSize: '12px' }}>
                        {editingUser.stripe_customer_id}
                      </code>
                    </div>
                  ) : (
                    <div>
                      <p style={{ color: '#666', marginBottom: '10px', fontSize: '13px' }}>
                        No Stripe customer ID. Create one to enable trials and subscriptions via Stripe.
                      </p>
                      <button
                        type="button"
                        onClick={handleCreateStripeCustomer}
                        disabled={creatingStripeCustomer}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: creatingStripeCustomer ? '#ccc' : '#6772e5',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: creatingStripeCustomer ? 'not-allowed' : 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        {creatingStripeCustomer ? 'Creating...' : 'Create Stripe Customer'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Script Queue Section */}
                {editingUser?.legacy_realm_id && (
                  <div style={{
                    marginTop: '20px',
                    marginBottom: '15px',
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                      Script Queue (Realm {editingUser.legacy_realm_id})
                    </label>

                    {loadingScripts ? (
                      <p style={{ color: '#666', fontStyle: 'italic' }}>Loading scripts...</p>
                    ) : queuedScripts.length === 0 ? (
                      <p style={{ color: '#666', fontStyle: 'italic' }}>No scripts in queue</p>
                    ) : (
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {queuedScripts.map((script, index) => (
                          <div
                            key={script.id}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '10px',
                              padding: '10px',
                              backgroundColor: index % 2 === 0 ? '#fff' : '#f5f5f5',
                              borderRadius: '4px',
                              marginBottom: '5px',
                              border: '1px solid #ddd'
                            }}
                          >
                            <span style={{
                              minWidth: '24px',
                              color: '#666',
                              fontWeight: 'bold',
                              fontSize: '12px'
                            }}>
                              #{index + 1}
                            </span>

                            {editingScriptId === script.id ? (
                              <div style={{ flex: 1 }}>
                                <textarea
                                  value={editingScriptValue}
                                  onChange={(e) => setEditingScriptValue(e.target.value)}
                                  style={{
                                    width: '100%',
                                    minHeight: '60px',
                                    padding: '8px',
                                    fontFamily: 'monospace',
                                    fontSize: '12px',
                                    border: '1px solid #4CAF50',
                                    borderRadius: '4px',
                                    boxSizing: 'border-box',
                                    resize: 'vertical'
                                  }}
                                />
                                <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
                                  <button
                                    type="button"
                                    onClick={() => handleSaveScript(script.id)}
                                    style={{
                                      padding: '4px 10px',
                                      backgroundColor: '#4CAF50',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px'
                                    }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleCancelEditScript}
                                    style={{
                                      padding: '4px 10px',
                                      backgroundColor: '#ccc',
                                      color: '#333',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px'
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div style={{ flex: 1 }}>
                                  <code style={{
                                    display: 'block',
                                    backgroundColor: '#e8e8e8',
                                    padding: '6px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    wordBreak: 'break-all',
                                    whiteSpace: 'pre-wrap'
                                  }}>
                                    {script.script_string}
                                  </code>
                                  <span style={{
                                    fontSize: '10px',
                                    color: '#888',
                                    marginTop: '4px',
                                    display: 'block'
                                  }}>
                                    Added: {new Date(script.created_at).toLocaleString()}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', gap: '5px' }}>
                                  <button
                                    type="button"
                                    onClick={() => handleEditScript(script)}
                                    style={{
                                      padding: '4px 8px',
                                      backgroundColor: '#2196F3',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px'
                                    }}
                                    title="Edit script"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteScript(script.id)}
                                    style={{
                                      padding: '4px 8px',
                                      backgroundColor: '#f44336',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px'
                                    }}
                                    title="Delete script"
                                  >
                                    Del
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '20px' }}>
                  <button
                    type="button"
                    onClick={handleDeleteUser}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Delete User
                  </button>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditUserModal(false);
                        setEditingUser(null);
                      }}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#ccc',
                        color: '#333',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                      }}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Player Utilization Modal */}
        {showUtilizationModal && (
          <div className="product-form-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#1a1a2e',
              color: '#e0e0e0',
              padding: '30px',
              borderRadius: '10px',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflow: 'auto',
              width: '95%',
              border: '1px solid #333'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: '#90EE90' }}>📈 Player Utilization</h3>
                <button
                  onClick={() => setShowUtilizationModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>

              {/* Date Range Selector */}
              <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#aaa' }}>Start Date</label>
                  <input
                    type="date"
                    value={utilizationDateRange.startDate}
                    onChange={(e) => setUtilizationDateRange({ ...utilizationDateRange, startDate: e.target.value })}
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #444',
                      backgroundColor: '#2a2a3e',
                      color: '#e0e0e0'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#aaa' }}>End Date</label>
                  <input
                    type="date"
                    value={utilizationDateRange.endDate}
                    onChange={(e) => setUtilizationDateRange({ ...utilizationDateRange, endDate: e.target.value })}
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #444',
                      backgroundColor: '#2a2a3e',
                      color: '#e0e0e0'
                    }}
                  />
                </div>
                <div style={{ alignSelf: 'flex-end' }}>
                  <button
                    onClick={fetchUtilizationData}
                    disabled={utilizationLoading}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#2c4a3e',
                      color: '#90EE90',
                      border: '1px solid #3d6b56',
                      borderRadius: '4px',
                      cursor: utilizationLoading ? 'not-allowed' : 'pointer',
                      opacity: utilizationLoading ? 0.6 : 1
                    }}
                  >
                    {utilizationLoading ? 'Loading...' : 'Update'}
                  </button>
                </div>
              </div>

              {/* Summary Stats */}
              {utilizationSummary && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: '15px',
                  marginBottom: '20px'
                }}>
                  <div style={{ backgroundColor: '#2a2a3e', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#90EE90' }}>{utilizationSummary.averagePlayers}</div>
                    <div style={{ fontSize: '12px', color: '#aaa' }}>Avg Players</div>
                  </div>
                  <div style={{ backgroundColor: '#2a2a3e', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4dabf7' }}>{utilizationSummary.maxPlayers}</div>
                    <div style={{ fontSize: '12px', color: '#aaa' }}>Peak Players</div>
                  </div>
                  <div style={{ backgroundColor: '#2a2a3e', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffd43b' }}>{utilizationSummary.minPlayers}</div>
                    <div style={{ fontSize: '12px', color: '#aaa' }}>Min Players</div>
                  </div>
                  <div style={{ backgroundColor: '#2a2a3e', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#e0e0e0' }}>{utilizationSummary.totalDataPoints}</div>
                    <div style={{ fontSize: '12px', color: '#aaa' }}>Data Points</div>
                  </div>
                </div>
              )}

              {/* Chart */}
              <div style={{ backgroundColor: '#2a2a3e', padding: '20px', borderRadius: '8px' }}>
                {utilizationLoading ? (
                  <div style={{ textAlign: 'center', padding: '60px', color: '#aaa' }}>
                    Loading chart data...
                  </div>
                ) : utilizationData.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px', color: '#aaa' }}>
                    No data available for the selected date range
                  </div>
                ) : (
                  <Line
                    data={{
                      labels: utilizationData.map(d => {
                        const date = new Date(d.timestamp);
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' });
                      }),
                      datasets: [{
                        label: 'Players Online',
                        data: utilizationData.map(d => d.playersOnline),
                        borderColor: '#90EE90',
                        backgroundColor: 'rgba(144, 238, 144, 0.1)',
                        fill: true,
                        tension: 0.3,
                        pointRadius: utilizationData.length > 100 ? 0 : 2,
                        pointHoverRadius: 5
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      aspectRatio: 2.5,
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          backgroundColor: '#1a1a2e',
                          titleColor: '#e0e0e0',
                          bodyColor: '#90EE90',
                          borderColor: '#333',
                          borderWidth: 1,
                          callbacks: {
                            title: (items) => {
                              if (items.length > 0) {
                                const date = new Date(utilizationData[items[0].dataIndex].timestamp);
                                return date.toLocaleString('en-US', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                });
                              }
                              return '';
                            },
                            label: (item) => `${item.raw} players online`
                          }
                        }
                      },
                      scales: {
                        x: {
                          display: true,
                          grid: {
                            color: 'rgba(255,255,255,0.1)'
                          },
                          ticks: {
                            color: '#aaa',
                            maxRotation: 45,
                            minRotation: 45,
                            maxTicksLimit: 12
                          }
                        },
                        y: {
                          display: true,
                          beginAtZero: true,
                          grid: {
                            color: 'rgba(255,255,255,0.1)'
                          },
                          ticks: {
                            color: '#aaa',
                            stepSize: 5
                          },
                          title: {
                            display: true,
                            text: 'Players Online',
                            color: '#aaa'
                          }
                        }
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* XP Analytics Modal */}
        {showXpAnalyticsModal && (
          <div className="product-form-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#1a1a2e',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '1400px',
              maxHeight: '90vh',
              overflow: 'auto',
              width: '95%',
              border: '1px solid #333'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: '#DDA0DD' }}>⚔️ XP Analytics</h3>
                <button
                  onClick={() => setShowXpAnalyticsModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>

              {/* Controls */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#aaa' }}>Length (hours)</label>
                  <input
                    type="number"
                    value={xpAnalyticsParams.lengthHours}
                    onChange={(e) => handleXpParamChange('lengthHours', parseInt(e.target.value) || 96)}
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #333',
                      backgroundColor: '#2a2a3e',
                      color: '#fff',
                      width: '80px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#aaa' }}>Window (minutes)</label>
                  <input
                    type="number"
                    value={xpAnalyticsParams.windowMinutes}
                    onChange={(e) => handleXpParamChange('windowMinutes', parseInt(e.target.value) || 15)}
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #333',
                      backgroundColor: '#2a2a3e',
                      color: '#fff',
                      width: '80px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#aaa' }}>Player ID</label>
                  <input
                    type="text"
                    value={xpAnalyticsParams.playerId}
                    onChange={(e) => handleXpParamChange('playerId', e.target.value)}
                    placeholder="Filter..."
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #333',
                      backgroundColor: '#2a2a3e',
                      color: '#fff',
                      width: '80px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#aaa' }}>Monster ID</label>
                  <input
                    type="text"
                    value={xpAnalyticsParams.monsterId}
                    onChange={(e) => handleXpParamChange('monsterId', e.target.value)}
                    placeholder="Filter..."
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #333',
                      backgroundColor: '#2a2a3e',
                      color: '#fff',
                      width: '80px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#aaa' }}>Location Range</label>
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={xpAnalyticsParams.locationMin}
                      onChange={(e) => handleXpParamChange('locationMin', e.target.value)}
                      placeholder="Min"
                      style={{
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #333',
                        backgroundColor: '#2a2a3e',
                        color: '#fff',
                        width: '60px'
                      }}
                    />
                    <span style={{ color: '#aaa' }}>-</span>
                    <input
                      type="text"
                      value={xpAnalyticsParams.locationMax}
                      onChange={(e) => handleXpParamChange('locationMax', e.target.value)}
                      placeholder="Max"
                      style={{
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #333',
                        backgroundColor: '#2a2a3e',
                        color: '#fff',
                        width: '60px'
                      }}
                    />
                  </div>
                </div>
                <div style={{ alignSelf: 'flex-end' }}>
                  <button
                    onClick={() => fetchXpAnalytics(xpAnalyticsParams)}
                    disabled={xpAnalyticsLoading}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#3e2c4a',
                      color: '#DDA0DD',
                      border: '1px solid #563d6b',
                      borderRadius: '4px',
                      cursor: xpAnalyticsLoading ? 'not-allowed' : 'pointer',
                      opacity: xpAnalyticsLoading ? 0.6 : 1
                    }}
                  >
                    {xpAnalyticsLoading ? 'Loading...' : 'Update'}
                  </button>
                </div>
              </div>

              {/* Summary Stats */}
              {xpAnalyticsData?.summary && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                  gap: '10px',
                  marginBottom: '20px'
                }}>
                  <div style={{ backgroundColor: '#2a2a3e', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#DDA0DD' }}>{xpAnalyticsData.summary.total_events || 0}</div>
                    <div style={{ fontSize: '11px', color: '#aaa' }}>Total Kills</div>
                  </div>
                  <div style={{ backgroundColor: '#2a2a3e', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#4dabf7' }}>{xpAnalyticsData.summary.unique_players || 0}</div>
                    <div style={{ fontSize: '11px', color: '#aaa' }}>Players</div>
                  </div>
                  <div style={{ backgroundColor: '#2a2a3e', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ffd43b' }}>{xpAnalyticsData.summary.unique_monsters || 0}</div>
                    <div style={{ fontSize: '11px', color: '#aaa' }}>Monsters</div>
                  </div>
                  <div style={{ backgroundColor: '#2a2a3e', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#90EE90' }}>{xpAnalyticsData.summary.unique_rooms || 0}</div>
                    <div style={{ fontSize: '11px', color: '#aaa' }}>Rooms</div>
                  </div>
                  <div style={{ backgroundColor: '#2a2a3e', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#e0e0e0' }}>{(xpAnalyticsData.summary.total_xp || 0).toLocaleString()}</div>
                    <div style={{ fontSize: '11px', color: '#aaa' }}>Total XP</div>
                  </div>
                  <div style={{ backgroundColor: '#2a2a3e', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ff6b6b' }}>{xpAnalyticsData.summary.avg_xp_per_kill || 0}</div>
                    <div style={{ fontSize: '11px', color: '#aaa' }}>Avg/Kill</div>
                  </div>
                </div>
              )}

              {/* Tab Navigation */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', borderBottom: '1px solid #333', paddingBottom: '10px' }}>
                <button
                  onClick={() => setXpActiveTab('players')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: xpActiveTab === 'players' ? '#3e2c4a' : 'transparent',
                    color: xpActiveTab === 'players' ? '#DDA0DD' : '#aaa',
                    border: xpActiveTab === 'players' ? '1px solid #563d6b' : '1px solid transparent',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Player XP/Window
                </button>
                <button
                  onClick={() => setXpActiveTab('rooms')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: xpActiveTab === 'rooms' ? '#3e2c4a' : 'transparent',
                    color: xpActiveTab === 'rooms' ? '#DDA0DD' : '#aaa',
                    border: xpActiveTab === 'rooms' ? '1px solid #563d6b' : '1px solid transparent',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Room Ranges
                </button>
                <button
                  onClick={() => setXpActiveTab('events')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: xpActiveTab === 'events' ? '#3e2c4a' : 'transparent',
                    color: xpActiveTab === 'events' ? '#DDA0DD' : '#aaa',
                    border: xpActiveTab === 'events' ? '1px solid #563d6b' : '1px solid transparent',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Raw Events
                </button>
              </div>

              {/* Tab Content */}
              <div style={{ backgroundColor: '#2a2a3e', padding: '15px', borderRadius: '8px' }}>
                {xpAnalyticsLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>
                    Loading data...
                  </div>
                ) : !xpAnalyticsData ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>
                    No data available
                  </div>
                ) : (
                  <>
                    {/* Players Tab - XP per Window */}
                    {xpActiveTab === 'players' && (
                      <div>
                        <div style={{ marginBottom: '10px', fontSize: '12px', color: '#aaa' }}>
                          Average XP earned per {xpAnalyticsData.windowAnalysis.windowMinutes}-minute window over {xpAnalyticsData.windowAnalysis.lengthHours} hours
                        </div>
                        {xpAnalyticsData.windowAnalysis.players.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>No player data found</div>
                        ) : (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid #444' }}>
                                  <th
                                    onClick={() => handlePlayerSort('character_id')}
                                    style={{ padding: '10px', textAlign: 'left', color: '#DDA0DD', cursor: 'pointer' }}
                                  >
                                    Character ID {xpPlayerSort.column === 'character_id' && (xpPlayerSort.dir === 'desc' ? '▼' : '▲')}
                                  </th>
                                  <th
                                    onClick={() => handlePlayerSort('player_level')}
                                    style={{ padding: '10px', textAlign: 'left', color: '#DDA0DD', cursor: 'pointer' }}
                                  >
                                    Level {xpPlayerSort.column === 'player_level' && (xpPlayerSort.dir === 'desc' ? '▼' : '▲')}
                                  </th>
                                  <th
                                    onClick={() => handlePlayerSort('avg_xp_per_window')}
                                    style={{ padding: '10px', textAlign: 'right', color: '#DDA0DD', cursor: 'pointer' }}
                                  >
                                    Avg XP/Window {xpPlayerSort.column === 'avg_xp_per_window' && (xpPlayerSort.dir === 'desc' ? '▼' : '▲')}
                                  </th>
                                  <th
                                    onClick={() => handlePlayerSort('active_windows')}
                                    style={{ padding: '10px', textAlign: 'right', color: '#DDA0DD', cursor: 'pointer' }}
                                  >
                                    Active Windows {xpPlayerSort.column === 'active_windows' && (xpPlayerSort.dir === 'desc' ? '▼' : '▲')}
                                  </th>
                                  <th
                                    onClick={() => handlePlayerSort('total_xp')}
                                    style={{ padding: '10px', textAlign: 'right', color: '#DDA0DD', cursor: 'pointer' }}
                                  >
                                    Total XP {xpPlayerSort.column === 'total_xp' && (xpPlayerSort.dir === 'desc' ? '▼' : '▲')}
                                  </th>
                                  <th
                                    onClick={() => handlePlayerSort('total_kills')}
                                    style={{ padding: '10px', textAlign: 'right', color: '#DDA0DD', cursor: 'pointer' }}
                                  >
                                    Total Kills {xpPlayerSort.column === 'total_kills' && (xpPlayerSort.dir === 'desc' ? '▼' : '▲')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {getSortedPlayers().map((player, idx) => (
                                  <tr key={player.character_id} style={{ borderBottom: '1px solid #333', backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                                    <td style={{ padding: '10px', color: '#4dabf7' }}>{player.character_id}</td>
                                    <td style={{ padding: '10px', color: '#ffd43b' }}>{player.player_level || '-'}</td>
                                    <td style={{ padding: '10px', textAlign: 'right', color: '#90EE90', fontWeight: 'bold' }}>{Number(player.avg_xp_per_window).toLocaleString()}</td>
                                    <td style={{ padding: '10px', textAlign: 'right', color: '#aaa' }}>{player.active_windows}</td>
                                    <td style={{ padding: '10px', textAlign: 'right', color: '#e0e0e0' }}>{Number(player.total_xp).toLocaleString()}</td>
                                    <td style={{ padding: '10px', textAlign: 'right', color: '#aaa' }}>{player.total_kills}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Room Ranges Tab */}
                    {xpActiveTab === 'rooms' && (
                      <div>
                        <div style={{ marginBottom: '10px', fontSize: '12px', color: '#aaa' }}>
                          Room ranges grouped by 100 and coalesced if contiguous
                        </div>
                        {xpAnalyticsData.roomRanges.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>No room data found</div>
                        ) : (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid #444' }}>
                                  <th
                                    onClick={() => handleRoomSort('range_start')}
                                    style={{ padding: '10px', textAlign: 'left', color: '#DDA0DD', cursor: 'pointer' }}
                                  >
                                    Room Range {xpRoomSort.column === 'range_start' && (xpRoomSort.dir === 'desc' ? '▼' : '▲')}
                                  </th>
                                  <th
                                    onClick={() => handleRoomSort('event_count')}
                                    style={{ padding: '10px', textAlign: 'right', color: '#DDA0DD', cursor: 'pointer' }}
                                  >
                                    Events {xpRoomSort.column === 'event_count' && (xpRoomSort.dir === 'desc' ? '▼' : '▲')}
                                  </th>
                                  <th
                                    onClick={() => handleRoomSort('total_xp')}
                                    style={{ padding: '10px', textAlign: 'right', color: '#DDA0DD', cursor: 'pointer' }}
                                  >
                                    Total XP {xpRoomSort.column === 'total_xp' && (xpRoomSort.dir === 'desc' ? '▼' : '▲')}
                                  </th>
                                  <th
                                    onClick={() => handleRoomSort('active_windows')}
                                    style={{ padding: '10px', textAlign: 'right', color: '#DDA0DD', cursor: 'pointer' }}
                                  >
                                    Active Windows {xpRoomSort.column === 'active_windows' && (xpRoomSort.dir === 'desc' ? '▼' : '▲')}
                                  </th>
                                  <th
                                    onClick={() => handleRoomSort('player_windows')}
                                    style={{ padding: '10px', textAlign: 'right', color: '#DDA0DD', cursor: 'pointer' }}
                                  >
                                    Player-Windows {xpRoomSort.column === 'player_windows' && (xpRoomSort.dir === 'desc' ? '▼' : '▲')}
                                  </th>
                                  <th
                                    onClick={() => handleRoomSort('unique_players_max')}
                                    style={{ padding: '10px', textAlign: 'right', color: '#DDA0DD', cursor: 'pointer' }}
                                  >
                                    Unique Players {xpRoomSort.column === 'unique_players_max' && (xpRoomSort.dir === 'desc' ? '▼' : '▲')}
                                  </th>
                                  <th
                                    onClick={() => handleRoomSort('sub_ranges')}
                                    style={{ padding: '10px', textAlign: 'right', color: '#DDA0DD', cursor: 'pointer' }}
                                  >
                                    Sub-Ranges {xpRoomSort.column === 'sub_ranges' && (xpRoomSort.dir === 'desc' ? '▼' : '▲')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {getSortedRooms().map((range, idx) => (
                                  <tr key={range.range_start} style={{ borderBottom: '1px solid #333', backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                                    <td style={{ padding: '10px', color: '#4dabf7', fontFamily: 'monospace' }}>
                                      {range.range_start} - {range.range_end}
                                    </td>
                                    <td style={{ padding: '10px', textAlign: 'right', color: '#aaa' }}>{Number(range.event_count).toLocaleString()}</td>
                                    <td style={{ padding: '10px', textAlign: 'right', color: '#90EE90', fontWeight: 'bold' }}>{Number(range.total_xp).toLocaleString()}</td>
                                    <td style={{ padding: '10px', textAlign: 'right', color: '#87CEEB' }}>{Number(range.active_windows).toLocaleString()}</td>
                                    <td style={{ padding: '10px', textAlign: 'right', color: '#FF69B4', fontWeight: 'bold' }}>{Number(range.player_windows).toLocaleString()}</td>
                                    <td style={{ padding: '10px', textAlign: 'right', color: '#ffd43b' }}>{range.unique_players_max}</td>
                                    <td style={{ padding: '10px', textAlign: 'right', color: '#666' }}>{range.sub_ranges}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Raw Events Tab */}
                    {xpActiveTab === 'events' && (
                      <div>
                        <div style={{ marginBottom: '10px', fontSize: '12px', color: '#aaa' }}>
                          Showing {xpAnalyticsData.events.length} of {xpAnalyticsData.pagination.totalCount} events
                        </div>
                        {xpAnalyticsData.events.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>No events found</div>
                        ) : (
                          <>
                            <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#2a2a3e' }}>
                                  <tr style={{ borderBottom: '1px solid #444' }}>
                                    <th
                                      onClick={() => handleXpSort('created_at')}
                                      style={{ padding: '8px', textAlign: 'left', color: '#DDA0DD', cursor: 'pointer' }}
                                    >
                                      Time {xpAnalyticsParams.sortBy === 'created_at' && (xpAnalyticsParams.sortDir === 'desc' ? '▼' : '▲')}
                                    </th>
                                    <th
                                      onClick={() => handleXpSort('data1')}
                                      style={{ padding: '8px', textAlign: 'left', color: '#DDA0DD', cursor: 'pointer' }}
                                    >
                                      Player {xpAnalyticsParams.sortBy === 'data1' && (xpAnalyticsParams.sortDir === 'desc' ? '▼' : '▲')}
                                    </th>
                                    <th
                                      onClick={() => handleXpSort('string1')}
                                      style={{ padding: '8px', textAlign: 'left', color: '#DDA0DD', cursor: 'pointer' }}
                                    >
                                      Level {xpAnalyticsParams.sortBy === 'string1' && (xpAnalyticsParams.sortDir === 'desc' ? '▼' : '▲')}
                                    </th>
                                    <th
                                      onClick={() => handleXpSort('data3')}
                                      style={{ padding: '8px', textAlign: 'right', color: '#DDA0DD', cursor: 'pointer' }}
                                    >
                                      XP {xpAnalyticsParams.sortBy === 'data3' && (xpAnalyticsParams.sortDir === 'desc' ? '▼' : '▲')}
                                    </th>
                                    <th
                                      onClick={() => handleXpSort('data4')}
                                      style={{ padding: '8px', textAlign: 'left', color: '#DDA0DD', cursor: 'pointer' }}
                                    >
                                      Monster {xpAnalyticsParams.sortBy === 'data4' && (xpAnalyticsParams.sortDir === 'desc' ? '▼' : '▲')}
                                    </th>
                                    <th
                                      onClick={() => handleXpSort('data5')}
                                      style={{ padding: '8px', textAlign: 'left', color: '#DDA0DD', cursor: 'pointer' }}
                                    >
                                      Room {xpAnalyticsParams.sortBy === 'data5' && (xpAnalyticsParams.sortDir === 'desc' ? '▼' : '▲')}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {xpAnalyticsData.events.map((event, idx) => (
                                    <tr key={event.id} style={{ borderBottom: '1px solid #333', backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                                      <td style={{ padding: '8px', color: '#aaa', fontSize: '11px' }}>
                                        {new Date(event.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                      </td>
                                      <td style={{ padding: '8px', color: '#4dabf7' }}>{event.character_id}</td>
                                      <td style={{ padding: '8px', color: '#ffd43b' }}>{event.player_level || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right', color: '#90EE90' }}>{event.xp_awarded.toLocaleString()}</td>
                                      <td style={{ padding: '8px', color: '#ff6b6b' }}>{event.monster_id}</td>
                                      <td style={{ padding: '8px', color: '#aaa', fontFamily: 'monospace' }}>{event.room_id}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {/* Pagination */}
                            {xpAnalyticsData.pagination.totalPages > 1 && (
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '15px' }}>
                                <button
                                  onClick={() => handleXpPageChange(1)}
                                  disabled={xpAnalyticsData.pagination.page === 1}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#333',
                                    color: xpAnalyticsData.pagination.page === 1 ? '#666' : '#fff',
                                    border: '1px solid #444',
                                    borderRadius: '4px',
                                    cursor: xpAnalyticsData.pagination.page === 1 ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  ««
                                </button>
                                <button
                                  onClick={() => handleXpPageChange(xpAnalyticsData.pagination.page - 1)}
                                  disabled={xpAnalyticsData.pagination.page === 1}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#333',
                                    color: xpAnalyticsData.pagination.page === 1 ? '#666' : '#fff',
                                    border: '1px solid #444',
                                    borderRadius: '4px',
                                    cursor: xpAnalyticsData.pagination.page === 1 ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  «
                                </button>
                                <span style={{ color: '#aaa', fontSize: '13px' }}>
                                  Page {xpAnalyticsData.pagination.page} of {xpAnalyticsData.pagination.totalPages}
                                </span>
                                <button
                                  onClick={() => handleXpPageChange(xpAnalyticsData.pagination.page + 1)}
                                  disabled={xpAnalyticsData.pagination.page === xpAnalyticsData.pagination.totalPages}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#333',
                                    color: xpAnalyticsData.pagination.page === xpAnalyticsData.pagination.totalPages ? '#666' : '#fff',
                                    border: '1px solid #444',
                                    borderRadius: '4px',
                                    cursor: xpAnalyticsData.pagination.page === xpAnalyticsData.pagination.totalPages ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  »
                                </button>
                                <button
                                  onClick={() => handleXpPageChange(xpAnalyticsData.pagination.totalPages)}
                                  disabled={xpAnalyticsData.pagination.page === xpAnalyticsData.pagination.totalPages}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#333',
                                    color: xpAnalyticsData.pagination.page === xpAnalyticsData.pagination.totalPages ? '#666' : '#fff',
                                    border: '1px solid #444',
                                    borderRadius: '4px',
                                    cursor: xpAnalyticsData.pagination.page === xpAnalyticsData.pagination.totalPages ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  »»
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;